import { createClient } from '@supabase/supabase-js';
import { RAIZ_APP } from './entrada';

const supabaseUrl  = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey  = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseKey);

// ═══════════════════════════════════════════════════════════════════════════════
// AUTH (Fase 3 — Supabase Auth)
// Login por correo + contraseña, recuperación, cambio de clave y perfil operativo.
// El objeto operativo de la app (currentUser) se arma desde `profiles` en App.js.
// `profiles.id === auth.uid()` → resolvemos el perfil por id (self-read por RLS).
// ═══════════════════════════════════════════════════════════════════════════════

// Login contra Supabase Auth. Devuelve { user, error } (error genérico para UX).
export async function signInWithEmail(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email: (email || '').trim().toLowerCase(),
    password,
  });
  if (error) return { user: null, error };
  return { user: data.user, error: null };
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) console.error('signOut error:', error);
}

// Envía el correo de recuperación. El enlace vuelve a la MISMA entrada desde la
// que se pidió (operaciones, /portal, organizaciones…): la sesión se guarda por
// dirección, así que volver a otra dejaría al usuario sin sesión.
// Cada dirección DEBE estar en la allow-list de Supabase (Auth → URL Configuration).
export async function sendPasswordReset(email) {
  const redirectTo = `${RAIZ_APP}/`;
  const { error } = await supabase.auth.resetPasswordForEmail(
    (email || '').trim().toLowerCase(),
    { redirectTo }
  );
  return { error };
}

// Cambia la contraseña del usuario autenticado. Por defecto limpia el flag de
// "clave temporal" en user_metadata en la MISMA llamada (sin migración ni RLS extra).
export async function updatePassword(newPassword, { clearMustChange = true } = {}) {
  const payload = { password: newPassword };
  if (clearMustChange) payload.data = { must_change_password: false };
  const { data, error } = await supabase.auth.updateUser(payload);
  return { user: data?.user ?? null, error };
}

// ─── Verificación en dos pasos (TOTP) — Lote B2 de la auditoría, nov-2026 ─────
// Factor de app autenticadora (Google Authenticator, Microsoft Authenticator…).
// Auth lleva el nivel de la sesión: aal1 = solo contraseña, aal2 = contraseña +
// código. Si el usuario tiene un factor verificado, el siguiente nivel es aal2 y
// App.js no deja pasar hasta que escriba el código (MfaCodigo.js). En el panel
// está encendido «Limit duration of AAL1 sessions»: Auth termina la sesión que
// no sube a aal2 en 15 min, así que el código también lo exige el servidor.

/** Nivel de la sesión: { actual: 'aal1'|'aal2', siguiente: 'aal1'|'aal2' }. */
export async function nivelMfa() {
  const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
  if (error) throw error;
  return { actual: data?.currentLevel || 'aal1', siguiente: data?.nextLevel || 'aal1' };
}

/** Factores TOTP del usuario: { verificados, pendientes }. */
export async function factoresTotp() {
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error) throw error;
  const todos = data?.all || [];
  const totp  = todos.filter(f => f.factor_type === 'totp');
  return {
    verificados: totp.filter(f => f.status === 'verified'),
    pendientes:  totp.filter(f => f.status !== 'verified'),
  };
}

/**
 * Empieza la activación: crea un factor sin verificar y devuelve el QR (SVG en
 * data URI) y la clave para escribirla a mano. Antes borra los intentos que
 * quedaron a medias, porque Auth no deja dos factores con el mismo nombre.
 */
export async function enrolarTotp() {
  const { pendientes } = await factoresTotp();
  for (const f of pendientes) await supabase.auth.mfa.unenroll({ factorId: f.id });
  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: 'totp',
    friendlyName: `Innovalab ${new Date().toISOString().slice(0, 10)}`,
    issuer: 'Innovalab',
  });
  if (error) throw error;
  return { factorId: data.id, qr: data.totp?.qr_code, clave: data.totp?.secret };
}

/** Verifica un código de 6 dígitos. Al acertar, la sesión sube a aal2. */
export async function verificarTotp(factorId, codigo) {
  const { error } = await supabase.auth.mfa.challengeAndVerify({
    factorId, code: String(codigo || '').replace(/\D/g, ''),
  });
  return { error };
}

/** Descarta un factor que no se terminó de activar. */
export async function cancelarTotp(factorId) {
  const { error } = await supabase.auth.mfa.unenroll({ factorId });
  return { error };
}

// Perfil operativo del usuario autenticado (tabla profiles, self-read por RLS).
// Columnas según Fase 1. OJO: la columna de nombre es `nombre` (se mapea a name en App.js).
export async function getMyProfile() {
  const { data: { user: au } } = await supabase.auth.getUser();
  if (!au) return null;
  const { data, error } = await supabase
    .from('profiles')
    .select('id, username, role, nombre, activo, sede_id, institucion_id, doctor_id, firma_url, sello_url, no_colegiado')
    .eq('id', au.id)
    .single();
  if (error) { console.error('getMyProfile error:', error); return null; }
  return data;
}

// Suscripción a cambios de sesión. Devuelve una función de limpieza (unsubscribe).
export function onAuthChange(cb) {
  const { data } = supabase.auth.onAuthStateChange((event, session) => cb(event, session));
  return () => data?.subscription?.unsubscribe();
}

// Sesión actual (para el bootstrap inicial, por si el evento INITIAL_SESSION no llega).
export async function getSession() {
  const { data } = await supabase.auth.getSession();
  return data?.session ?? null;
}

// Bearer para llamar Edge Functions: el access_token de la sesión, NO la anon key.
//
// La anon key identifica al proyecto, no a la persona: con ella auth.uid() llega
// null del otro lado. Eso fue el incidente del 2026-08-01 18:31 — la v10 de
// generate-pdf agregó cobro de cuota, la RPC exigía auth.uid() y el 100% de las
// descargas devolvió 403 (ver el encabezado de supabase/functions/generate-pdf).
//
// El fallback a la anon key es deliberado: generate-pdf hoy no decide quién ve
// qué (eso lo decide la RLS de `results`), así que una sesión aún no hidratada
// no debe romper una descarga. Cualquier función que SÍ mire al usuario tiene
// que rechazar el fallback del lado del servidor, no confiar en este helper.
//
// Sin sesión en memoria se intenta UNA renovación (refreshSession) antes del
// fallback: cuando generate-pdf exija sesión (GENERATE_PDF_EXIGIR_SESION), la
// anon key ya no alcanza y una sesión vencida no debe sentirse como "sin acceso".
export async function tokenEdge() {
  const { data } = await supabase.auth.getSession();
  let token = data?.session?.access_token;
  if (!token) {
    try {
      const { data: renovada } = await supabase.auth.refreshSession();
      token = renovada?.session?.access_token;
    } catch { /* sin sesión renovable: cae a la anon key */ }
  }
  return token || process.env.REACT_APP_SUPABASE_ANON_KEY || '';
}

// ═══════════════════════════════════════════════════════════════════════════════
// USUARIOS Y ORGANIZACIONES
//
// La identidad vive en Supabase Auth (`auth.users`) + `profiles`. La tabla
// heredada `users`, con contraseñas en texto plano, quedó retirada: no
// autenticaba nada desde la migración a Auth y era un pasivo de seguridad.
//
// Las ALTAS y los cambios de contraseña pasan por la Edge Function
// `admin-usuarios`, porque la API de administración de Auth exige la llave
// `service_role` y esa llave no puede bajar al navegador. La autorización
// (quién puede crear a quién) se resuelve del lado del servidor.
// ═══════════════════════════════════════════════════════════════════════════════

/** Invoca `admin-usuarios` con la sesión actual. Devuelve el cuerpo ya parseado. */
async function llamarAdminUsuarios(payload) {
  const { data, error } = await supabase.functions.invoke('admin-usuarios', { body: payload });
  if (error) {
    // supabase-js envuelve el cuerpo del error; se rescata para no perder el motivo.
    let detalle = error.message;
    try {
      const cuerpo = await error.context?.json?.();
      if (cuerpo?.error) detalle = cuerpo.detalle ? `${cuerpo.error}: ${cuerpo.detalle}` : cuerpo.error;
    } catch { /* el cuerpo no era JSON; queda el mensaje original */ }
    throw new Error(detalle);
  }
  if (data?.error) throw new Error(data.detalle ? `${data.error}: ${data.detalle}` : data.error);
  return data;
}

/**
 * Crea un usuario.
 * @param {'invitacion'|'temporal'} modo - invitación por correo (la persona
 *        define su clave y nadie más la conoce) o clave temporal con cambio
 *        obligatorio al entrar (para quien no tiene correo propio).
 */
export function crearUsuarioAdmin({ email, nombre, role, modo = 'invitacion', institucionId = null, doctorId = null, password = null }) {
  return llamarAdminUsuarios({
    accion: 'crear', email, nombre, role, modo,
    institucion_id: institucionId, doctor_id: doctorId, password,
  });
}

export function resetearPasswordAdmin({ perfilId, modo = 'temporal', password = null }) {
  return llamarAdminUsuarios({ accion: 'resetear_password', perfil_id: perfilId, modo, password });
}

export function setUsuarioActivo({ perfilId, activo }) {
  return llamarAdminUsuarios({ accion: 'set_activo', perfil_id: perfilId, activo });
}

export function cambiarRolUsuario({ perfilId, role }) {
  return llamarAdminUsuarios({ accion: 'cambiar_rol', perfil_id: perfilId, role });
}

/** Perfiles del staff interno del laboratorio (admin/qb/op). */
export async function getStaffInterno() {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, username, nombre, role, activo, created_at')
    .in('role', ['admin', 'qb', 'op', 'socio'])
    .order('role')
    .order('nombre');
  if (error) throw error;
  return data || [];
}

// ─── Organizaciones ──────────────────────────────────────────────────────────

export async function getOrganizaciones() {
  const { data, error } = await supabase.rpc('get_organizaciones');
  if (error) throw error;
  return data || [];
}

export async function getPersonalOrganizacion(institucionId) {
  if (!institucionId) return [];
  const { data, error } = await supabase.rpc('get_personal_organizacion', { p_institucion_id: institucionId });
  if (error) throw error;
  return data || [];
}

export async function asignarAgendaSecretaria({ secretariaId, doctorId, puedeAgendar = true, puedeCancelar = true }) {
  const { data, error } = await supabase.rpc('asignar_agenda_secretaria', {
    p_secretaria_id: secretariaId,
    p_doctor_id: doctorId,
    p_puede_agendar: puedeAgendar,
    p_puede_cancelar: puedeCancelar,
  });
  if (error) throw error;
  return data;
}

export async function revocarAgendaSecretaria(asignacionId) {
  const { error } = await supabase.rpc('revocar_agenda_secretaria', { p_asignacion_id: asignacionId });
  if (error) throw error;
}

export async function getIndicadoresInstitucion({ institucionId, desde = null, hasta = null }) {
  const { data, error } = await supabase.rpc('get_indicadores_institucion', {
    p_institucion_id: institucionId, p_desde: desde, p_hasta: hasta,
  });
  if (error) throw error;
  return data || [];
}

export async function getFacturacionInstitucion({ institucionId, desde = null, hasta = null }) {
  const { data, error } = await supabase.rpc('get_facturacion_institucion', {
    p_institucion_id: institucionId, p_desde: desde, p_hasta: hasta,
  });
  if (error) throw error;
  return data || [];
}

// ═══════════════════════════════════════════════════════════════════════════════
// CORRELATIVO DE ÓRDENES
// Desde el Lote 6 la base asigna el número al insertar la orden y avanza el
// contador; setCorrelativo queda solo para el ajuste manual (serie nueva). La
// base rechaza un contador que retrocede o con formato inválido.
// ═══════════════════════════════════════════════════════════════════════════════
export async function getCorrelativo() {
  const { data, error } = await supabase
    .from('settings')
    .select('value')
    .eq('key', 'correlativo')
    .single();
  if (error) { console.error('getCorrelativo error:', error); return '0001'; }
  return data?.value || '0001';
}

export async function setCorrelativo(value) {
  const { error } = await supabase
    .from('settings')
    .upsert({ key: 'correlativo', value });
  if (error) { console.error('setCorrelativo error:', error); return error.message || 'No se pudo guardar el No. de orden.'; }
  return null;
}

// ═══════════════════════════════════════════════════════════════════════════════
// CORRELATIVO DE CONTRASEÑAS (C-####) — nuevo
// Guardado en settings con key 'correlativo_contrasena'
// ═══════════════════════════════════════════════════════════════════════════════
export async function getCorrContrasena() {
  const { data, error } = await supabase
    .from('settings')
    .select('value')
    .eq('key', 'correlativo_contrasena')
    .single();
  if (error) { return 'C-0001'; }
  return data?.value || 'C-0001';
}

export async function setCorrContrasena(value) {
  const { error } = await supabase
    .from('settings')
    .upsert({ key: 'correlativo_contrasena', value });
  if (error) console.error('setCorrContrasena error:', error);
}

// ═══════════════════════════════════════════════════════════════════════════════
// RECORDS (órdenes de trabajo) — sin cambios + campo estado
// ═══════════════════════════════════════════════════════════════════════════════
// Trae TODAS las filas de una consulta en tramos de 1000. PostgREST corta en
// silencio en db-max-rows (1000): sin esto, la orden 1001 simplemente no
// aparecería en Registros ni en Resultados, sin error. `armar` recibe nada y
// devuelve la consulta ya ordenada (con desempate por id para que los tramos
// no se pisen ni dejen huecos).
const TRAMO = 1000; // debe ser == db-max-rows del proyecto
async function traerTodo(armar, etiqueta) {
  let filas = [];
  for (let from = 0; ; from += TRAMO) {
    const { data, error } = await armar().range(from, from + TRAMO - 1);
    if (error) { console.error(`${etiqueta} error:`, error); return from === 0 ? [] : filas; }
    if (!data?.length) break;
    filas = filas.concat(data);
    if (data.length < TRAMO) break;
  }
  return filas;
}

export async function getRecords() {
  return traerTodo(() => supabase
    .from('records')
    .select('*')
    .order('created_at', { ascending: false })
    .order('id', { ascending: false }), 'getRecords');
}

export async function insertRecord(record) {
  // Asegura que el campo estado esté presente
  const payload = { estado: 'pendiente', ...record };
  const { data, error } = await supabase
    .from('records')
    .insert(payload)
    .select();
  if (error) throw error;
  return data[0];
}

export async function updateRecord(correlativo, updates) {
  const { error } = await supabase
    .from('records')
    .update({ ...updates, edited_at: new Date().toISOString() })
    .eq('correlativo', correlativo);
  if (error) throw error;
}

export async function updateRecordEstado(correlativo, estado) {
  const { error } = await supabase
    .from('records')
    .update({ estado, edited_at: new Date().toISOString() })
    .eq('correlativo', correlativo);
  if (error) throw error;
}

export async function deleteRecord(correlativo) {
  // Un solo DELETE, atómico (Lote 6): la FK results → records es ON DELETE CASCADE
  // y result_values → results también, así que la cabecera y los valores se van
  // con la orden. Una orden con envíos registrados (distribution_logs) no se borra:
  // su FK es RESTRICT y la base rechaza todo el borrado, sin dejar nada a medias.
  const { error } = await supabase
    .from('records')
    .delete()
    .eq('correlativo', correlativo);
  if (error) {
    if (error.code === '23503') throw new Error('Esta orden ya tiene envíos de resultado registrados y no se puede eliminar.');
    throw error;
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// CATALOG & ORDER — sin cambios
// ═══════════════════════════════════════════════════════════════════════════════
export async function getCatalog() {
  const { data, error } = await supabase
    .from('settings')
    .select('value')
    .eq('key', 'catalog')
    .single();
  if (error || !data) return null;
  try { return JSON.parse(data.value); } catch(e) { return null; }
}

export async function saveCatalog(catalog) {
  const { error } = await supabase
    .from('settings')
    .upsert({ key: 'catalog', value: JSON.stringify(catalog) });
  if (error) console.error('saveCatalog error:', error);
}

export async function getCatOrder() {
  const { data, error } = await supabase
    .from('settings')
    .select('value')
    .eq('key', 'catOrder')
    .single();
  if (error || !data) return null;
  try { return JSON.parse(data.value); } catch(e) { return null; }
}

export async function saveCatOrder(order) {
  const { error } = await supabase
    .from('settings')
    .upsert({ key: 'catOrder', value: JSON.stringify(order) });
  if (error) console.error('saveCatOrder error:', error);
}

// ═══════════════════════════════════════════════════════════════════════════════
// DOCTORS (directorio de médicos) — nuevo
//
// Tabla: doctors
// Columnas:
//   id            uuid (PK, default gen_random_uuid())
//   codigo        text  UNIQUE  — generado automáticamente: MD-0001
//   nombre        text
//   apellido      text
//   telefono      text
//   correo        text
//   colegiado     text
//   observaciones text
//   descuento     numeric default 0   — % de descuento especial (solo admin)
//   updated_by    text                — auditoría: usuario que hizo el último cambio
//   created_at    timestamptz default now()
//   deleted       boolean default false
// ═══════════════════════════════════════════════════════════════════════════════
export async function getDoctors() {
  const { data, error } = await supabase
    .from('doctors')
    .select('*')
    .eq('deleted', false)
    .order('apellido', { ascending: true });
  if (error) { console.error('getDoctors error:', error); return []; }
  return data || [];
}

export async function getDoctorByCode(codigo) {
  const { data, error } = await supabase
    .from('doctors')
    .select('*')
    .eq('codigo', codigo)
    .eq('deleted', false)
    .single();
  if (error) return null;
  return data;
}

// Genera el siguiente código MD-####
export async function getNextDoctorCode() {
  const { data, error } = await supabase
    .from('settings')
    .select('value')
    .eq('key', 'last_doctor_code')
    .single();

  let last = 0;
  if (!error && data?.value) last = parseInt(data.value, 10) || 0;
  const next = last + 1;

  await supabase
    .from('settings')
    .upsert({ key: 'last_doctor_code', value: String(next) });

  return `MD-${String(next).padStart(4, '0')}`;
}

export async function insertDoctor(doctor) {
  // El código se genera antes de llamar a esta función
  const { data, error } = await supabase
    .from('doctors')
    .insert({ ...doctor, deleted: false })
    .select();
  if (error) throw error;
  return data[0];
}

export async function updateDoctor(id, updates) {
  const { error } = await supabase
    .from('doctors')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id);
  if (error) throw error;
}

export async function softDeleteDoctor(id) {
  const { error } = await supabase
    .from('doctors')
    .update({ deleted: true })
    .eq('id', id);
  if (error) throw error;
}

// Ordenes asociadas a un médico (para historial e informe)
export async function getRecordsByDoctor(doctorName) {
  // Busca por nombre completo del médico tal como se guarda en records
  const { data, error } = await supabase
    .from('records')
    .select('correlativo, created_at, paciente, total, examenes, factura')
    .ilike('medico->>nombre', `%${doctorName}%`)
    .order('created_at', { ascending: false });
  if (error) { console.error('getRecordsByDoctor error:', error); return []; }
  return data || [];
}

// ═══════════════════════════════════════════════════════════════════════════════
// INSTITUCIONES — referentes (grupo de comisión) y/o convenio (crédito/visibilidad)
//
// Tabla: instituciones
// Columnas:
//   id           uuid (PK, default gen_random_uuid())
//   nombre       text   not null
//   direccion    text
//   es_referente boolean default false  — grupo de comisión (doctors.institucion_id)
//   es_convenio  boolean default false  — crédito/pertenencia (records.institucion_id)
//   descuento    numeric default 0 (0–100) — % de descuento de convenio (solo es_convenio)
//   activo       boolean default true   — soft delete
//   created_at   timestamptz default now()
//
// Una misma institución puede ser referente, convenio o ambas. Los FK NO se cruzan:
//   doctors.institucion_id  → solo referentes
//   records.institucion_id  → solo convenios
// ═══════════════════════════════════════════════════════════════════════════════
export async function getInstituciones({ soloActivas = true } = {}) {
  let query = supabase
    .from('instituciones')
    .select('*')
    .order('nombre', { ascending: true });
  if (soloActivas) query = query.eq('activo', true);
  const { data, error } = await query;
  if (error) { console.error('getInstituciones error:', error); return []; }
  return data || [];
}

/**
 * Traduce los rechazos de la base al escribir una organización.
 *
 * El nombre se usó durante años como llave de facturación (`comprobantes_credito`
 * lo guardaba en texto), así que hay un índice único sobre el nombre en minúsculas
 * entre las activas. El mensaje crudo de Postgres no le dice nada a nadie.
 */
function traducirErrorInstitucion(error) {
  const txt = `${error.message || ''} ${error.details || ''}`.toLowerCase();
  if (txt.includes('instituciones_nombre_lower_uq')) {
    return new Error('Ya existe una organización activa con ese nombre. Si la habías desactivado, reactívala en vez de crear otra.');
  }
  if (error.code === '42501' || txt.includes('row-level security')) {
    return new Error('No tienes permiso para modificar organizaciones. Solo un administrador puede hacerlo.');
  }
  return new Error(error.message || 'No se pudo guardar la organización.');
}

/** Campos permitidos al escribir. Se filtra a propósito: nunca se manda el objeto
 *  del formulario entero, para que un campo extra no viaje sin querer. */
function saneaInstitucion(inst) {
  const esConvenio = !!inst.es_convenio;
  const desc = Number(inst.descuento);
  return {
    nombre: (inst.nombre || '').trim(),
    direccion: (inst.direccion || '').trim() || null,
    es_referente: !!inst.es_referente,
    es_convenio: esConvenio,
    // Fuera de convenio el descuento no significa nada: se normaliza a 0 acá
    // también, no solo en la pantalla, para que no dependa de la interfaz.
    descuento: esConvenio && Number.isFinite(desc) ? Math.min(100, Math.max(0, desc)) : 0,
    activo: inst.activo !== false,
  };
}

export async function insertInstitucion(inst) {
  const { data, error } = await supabase
    .from('instituciones')
    .insert(saneaInstitucion(inst))
    .select()
    .single();
  if (error) throw traducirErrorInstitucion(error);
  return data;
}

export async function updateInstitucion(id, updates) {
  // Si viene la ficha completa se sanea; si es un cambio puntual (por ejemplo
  // solo `activo`), se respeta tal cual.
  const payload = 'nombre' in updates ? saneaInstitucion(updates) : updates;
  const { data, error } = await supabase
    .from('instituciones')
    .update(payload)
    .eq('id', id)
    .select()
    .single();
  if (error) throw traducirErrorInstitucion(error);
  return data;
}

export async function softDeleteInstitucion(id) {
  const { error } = await supabase
    .from('instituciones')
    .update({ activo: false })
    .eq('id', id);
  if (error) throw error;
}

// ═══════════════════════════════════════════════════════════════════════════════
// RESULTS (cabecera de resultado) + RESULT_VALUES (parámetros relacionales)
//
// Tabla results:
//   id, correlativo (UNIQUE), estado, ingresado_por, aprobado_por,
//   firma_url, notas_qb, created_at, updated_at
//   (columna valores jsonb queda deprecated — NO se escribe más)
//
// Tabla result_values:
//   id, correlativo (FK), param_id, exam_name, origen_exam, cat, subcat,
//   tipo, valor, flag, ref_texto, ref_min, ref_max, unidad, decimales,
//   opciones (jsonb), tiene_obs, obs_extra, obs_examen, sort_order
// ═══════════════════════════════════════════════════════════════════════════════

// ── Resultados por demanda (24-oct-2026) ─────────────────────────────────────
// La carga inicial ya NO baja result_values (eran ~6,000 filas, ~4 MB, y
// crecían ~1,650 al mes). Las listas trabajan con cabeceras + conteos que
// calcula la base (lab_resultados_cabeceras):
//   n_valores, n_llenos (progreso x/y), n_pendientes, n_distribuciones.
// Los valores de UN resultado se piden solo al abrirlo o al sacar su PDF
// (getValoresDe / getResultadoConValores).

/** Quita la columna `valores` (jsonb deprecated) si vino en un aviso de realtime. */
export function soloCabecera(r) {
  if (!r) return r;
  const { valores, ...cab } = r; // eslint-disable-line no-unused-vars
  return cab;
}

/**
 * Cabeceras de resultados con sus conteos.
 *   correlativos: solo esos (null = todos los visibles)
 *   desde:        solo lo que cambió desde ese momento (ISO), para el poll
 * Devuelve { ahora, filas }; `ahora` es el cursor del siguiente poll.
 */
export async function getCabecerasResultados({ correlativos = null, desde = null } = {}) {
  const { data, error } = await supabase.rpc('lab_resultados_cabeceras', {
    p_correlativos: correlativos, p_desde: desde,
  });
  if (error) { console.error('getCabecerasResultados error:', error); throw error; }
  return { ahora: data?.ahora || null, filas: data?.filas || [] };
}

/** Todas las cabeceras visibles (sin valores). */
export async function getResultHeaders() {
  return (await getCabecerasResultados()).filas;
}

/** Ordena los valores de un resultado (el orden del servidor entre empates no es garantizado). */
const ordenarValores = (vs) => vs.sort((a, b) =>
  (a.sort_order ?? 0) - (b.sort_order ?? 0) || (a.id ?? 0) - (b.id ?? 0));

/**
 * Valores de algunos resultados, agrupados por correlativo: { [corr]: [...] }.
 * Para abrir un resultado o generar su PDF, sin bajar el historial completo.
 */
export async function getValoresDe(correlativos) {
  const lista = [...new Set((correlativos || []).filter(Boolean))];
  const porCorr = Object.fromEntries(lista.map(c => [c, []]));
  const LOTE = 50; // correlativos por consulta: la lista viaja en la URL
  for (let i = 0; i < lista.length; i += LOTE) {
    const lote = lista.slice(i, i + LOTE);
    const filas = await traerTodo(() => supabase
      .from('result_values')
      .select('*')
      .in('correlativo', lote)
      .order('correlativo', { ascending: true })
      .order('sort_order',  { ascending: true })
      .order('id',          { ascending: true }), 'getValoresDe');
    for (const v of filas) (porCorr[v.correlativo] = porCorr[v.correlativo] || []).push(v);
  }
  Object.values(porCorr).forEach(ordenarValores);
  return porCorr;
}

/**
 * Un resultado listo para abrir o imprimir: cabecera FRESCA de la base +
 * sus valores. Siempre consulta (no confía en lo que haya en memoria), así el
 * estado y los valores son los vigentes. null si la orden no tiene resultado.
 */
export async function getResultadoConValores(correlativo) {
  if (!correlativo) return null;
  const [{ filas }, valores] = await Promise.all([
    getCabecerasResultados({ correlativos: [correlativo] }),
    getValoresDe([correlativo]),
  ]);
  const cab = filas[0];
  if (!cab) return null;
  return { ...cab, valores: valores[correlativo] || [] };
}

/** Recalcula los conteos de una cabecera a partir de sus valores recién guardados. */
export function cabeceraConConteos(resultado) {
  const vs = resultado?.valores;
  const cab = soloCabecera(resultado);
  if (!Array.isArray(vs)) return cab;
  return {
    ...cab,
    n_valores:    vs.length,
    n_llenos:     vs.filter(v => v.valor !== '' && v.valor !== null).length,
    n_pendientes: vs.filter(v => v.pendiente).length,
  };
}

/** Compatibilidad: la carga ya no trae valores, solo cabeceras con conteos. */
export async function getResults() {
  return getResultHeaders();
}

/**
 * Órdenes con su resultado, paginadas en el servidor (lab_ordenes_pag).
 *   busqueda: paciente o correlativo (sin tildes ni mayúsculas)
 *   estado:   estado del resultado ('aprobado', … , 'sin_resultados'); null = todos
 *   desde / hasta: 'YYYY-MM-DD' (día de Guatemala, inclusive)
 * Devuelve { filas: [orden con .resultado], total }.
 */
export async function getOrdenesPag({ busqueda = '', estado = null, desde = null, hasta = null, limite = 20, offset = 0 } = {}) {
  const { data, error } = await supabase.rpc('lab_ordenes_pag', {
    p_busqueda: busqueda || null, p_estado: estado, p_desde: desde, p_hasta: hasta,
    p_limite: limite, p_offset: offset,
  });
  if (error) { console.error('getOrdenesPag error:', error); throw error; }
  const filas = (data || []).map(f => ({ ...f.orden, resultado: f.resultado || null }));
  return { filas, total: Number(data?.[0]?.total || 0) };
}

export async function getResultByCorrelativo(correlativo) {
  const { data: header, error: hErr } = await supabase
    .from('results')
    .select('*')
    .eq('correlativo', correlativo)
    .single();
  if (hErr) return null;

  const { data: vals } = await supabase
    .from('result_values')
    .select('*')
    .eq('correlativo', correlativo)
    .order('sort_order', { ascending: true });

  return { ...header, valores: vals || [] };
}

// Guarda cabecera + parámetros. valores = array flat igual que antes.
export async function upsertResult({ correlativo, estado, valores, ingresado_por, updated_at }) {
  // 1. Upsert cabecera (sin escribir valores jsonb)
  const { data: header, error: hErr } = await supabase
    .from('results')
    .upsert(
      { correlativo, estado, ingresado_por, updated_at: updated_at || new Date().toISOString() },
      { onConflict: 'correlativo' }
    )
    .select('id,correlativo,estado,ingresado_por,aprobado_por,firma_url,notas_qb,es_parcial,version,created_at,updated_at')
    .single();
  if (hErr) throw hErr;

  // 2. Borrar valores anteriores y reinsertar (más simple que upsert por param)
  const { error: delErr } = await supabase
    .from('result_values')
    .delete()
    .eq('correlativo', correlativo);
  if (delErr) throw delErr;

  if (valores?.length) {
    const rows = valores.map((v, i) => ({
      correlativo,
      param_id:    v.id || v.param_id || null,
      exam_name:   v.exam_name   || null,
      origen_exam: v.origen_exam || v.cat || null,
      cat:         v.cat         || null,
      subcat:      v.subcat      || null,
      tipo:        v.tipo        || null,
      valor:       v.valor !== undefined ? String(v.valor) : null,
      flag:        v.flag        || 'normal',
      ref_texto:   v.ref_texto   || null,
      ref_min:     v.ref_min     != null ? Number(v.ref_min)  : null,
      ref_max:     v.ref_max     != null ? Number(v.ref_max)  : null,
      unidad:      v.unidad      || null,
      decimales:   v.decimales   != null ? Number(v.decimales) : null,
      opciones:    v.opciones    || null,
      tiene_obs:   v.tieneObs    || v.tiene_obs || false,
      obs_extra:   v.obs_extra   || null,
      obs_examen:  v.obs_examen  || null,
      bandas:      v.bandas      || null,
      pendiente:   !!v.pendiente,          // resultado parcial (oct-2026)
      sort_order:  i,
    }));

    const { error: insErr } = await supabase
      .from('result_values')
      .insert(rows);
    if (insErr) throw insErr;
  }

  // Devolver cabecera + valores recién insertados (mismo shape que antes)
  const { data: vals } = await supabase
    .from('result_values')
    .select('*')
    .eq('correlativo', correlativo)
    .order('sort_order', { ascending: true });

  return { ...header, valores: vals || [] };
}

export async function updateResultEstado(correlativo, estado, extra = {}) {
  const { error } = await supabase
    .from('results')
    .update({ estado, updated_at: new Date().toISOString(), ...extra })
    .eq('correlativo', correlativo);
  if (error) throw error;
}

// Parámetros de referencia por defecto — guardados en settings
export async function getRefParams() {
  const { data, error } = await supabase
    .from('settings')
    .select('value')
    .eq('key', 'ref_params')
    .single();
  if (error || !data) return [];
  try { return JSON.parse(data.value); } catch { return []; }
}

export async function saveRefParams(params) {
  const { error } = await supabase
    .from('settings')
    .upsert({ key: 'ref_params', value: JSON.stringify(params) });
  if (error) console.error('saveRefParams error:', error);
}

// Firma del QB — subida a Supabase Storage (bucket: 'signatures')
// Retorna la URL pública de la imagen subida
export async function uploadQBSignature(userId, file) {
  const path = `qb_${userId}_${Date.now()}.png`;
  const { error: upErr } = await supabase.storage
    .from('signatures')
    .upload(path, file, { upsert: true, contentType: file.type });
  if (upErr) throw upErr;

  const { data } = supabase.storage
    .from('signatures')
    .getPublicUrl(path);
  return data.publicUrl;
}

export async function getQBSignatureUrl(userId) {
  const { data, error } = await supabase
    .from('settings')
    .select('value')
    .eq('key', `qb_signature_${userId}`)
    .single();
  if (error || !data) return null;
  return data.value;
}

export async function saveQBSignatureUrl(userId, url) {
  const { error } = await supabase
    .from('settings')
    .upsert({ key: `qb_signature_${userId}`, value: url });
  if (error) console.error('saveQBSignatureUrl error:', error);
}

// ═══════════════════════════════════════════════════════════════════════════════
// QUOTATIONS (cotizaciones) — nuevo
//
// Tabla: quotations
// Columnas:
//   id           uuid (PK)
//   codigo       text  — COT-####, generado automáticamente
//   descripcion  text  — nombre del paciente o descripción libre
//   examenes     jsonb — array de exámenes cotizados
//   total        numeric
//   created_by   text  — username
//   created_at   timestamptz default now()
//   deleted      boolean default false
// ═══════════════════════════════════════════════════════════════════════════════
export async function getQuotations() {
  return traerTodo(() => supabase
    .from('quotations')
    .select('*')
    .eq('deleted', false)
    .order('created_at', { ascending: false })
    .order('id', { ascending: false }), 'getQuotations');
}

export async function getNextQuotationCode() {
  // El número lo entrega la base en UN solo paso (siguiente_codigo_cotizacion):
  // lo toma y lo avanza a la vez. Antes se leía y luego se escribía, y dos
  // cotizaciones creadas en el mismo instante podían salir con el mismo COT
  // (pasó con COT-0124). Betty usa la misma función, así que comparten contador
  // sin chocar.
  const { data: codigo, error: rpcError } = await supabase.rpc('siguiente_codigo_cotizacion');
  if (!rpcError && codigo) return codigo;
  console.error('siguiente_codigo_cotizacion falló; se usa el método anterior:', rpcError);

  // Respaldo: el método anterior (leer y escribir), por si la función no está.
  const { data, error } = await supabase
    .from('settings')
    .select('value')
    .eq('key', 'last_quotation_code')
    .single();

  let last = 0;
  if (!error && data?.value) last = parseInt(data.value, 10) || 0;
  const next = last + 1;

  await supabase
    .from('settings')
    .upsert({ key: 'last_quotation_code', value: String(next) });

  return `COT-${String(next).padStart(4, '0')}`;
}

export async function insertQuotation(quotation) {
  const { data, error } = await supabase
    .from('quotations')
    .insert({ ...quotation, deleted: false })
    .select();
  if (error) throw error;
  return data[0];
}

export async function softDeleteQuotation(id) {
  const { error } = await supabase
    .from('quotations')
    .update({ deleted: true })
    .eq('id', id);
  if (error) throw error;
}

export async function deleteQuotations(ids) {
  const { error } = await supabase
    .from('quotations')
    .delete()
    .in('id', ids);
  if (error) throw error;
}

// ═══════════════════════════════════════════════════════════════════════════════
// PASSWORDS / CONTRASEÑAS (pacientes con habitación asignada) — nuevo
//
// Tabla: passwords
// Columnas:
//   id           uuid (PK)
//   correlativo  text UNIQUE  — C-0001
//   paciente     text         — nombre del paciente
//   habitacion   text         — habitación/cama
//   examenes     jsonb        — exámenes de la orden asociada
//   total        numeric      — total con descuento ya aplicado
//   descuento    numeric      — monto del convenio
//   orden_corr   text         — correlativo de la orden de trabajo vinculada
//   estado       text         — 'activa' | 'usada' | 'anulada'
//   created_by   text
//   created_at   timestamptz default now()
// ═══════════════════════════════════════════════════════════════════════════════
export async function getPasswords() {
  const { data, error } = await supabase
    .from('passwords')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) { console.error('getPasswords error:', error); return []; }
  return data || [];
}

export async function insertPassword(pw) {
  const { data, error } = await supabase
    .from('passwords')
    .insert(pw)
    .select();
  if (error) throw error;
  return data[0];
}

export async function updatePasswordEstado(correlativo, estado) {
  const { error } = await supabase
    .from('passwords')
    .update({ estado })
    .eq('correlativo', correlativo);
  if (error) throw error;
}

export async function deletePassword(correlativo) {
  const { error } = await supabase
    .from('passwords')
    .delete()
    .eq('correlativo', correlativo);
  if (error) throw error;
}

export async function deletePasswords(correlativos) {
  const { error } = await supabase
    .from('passwords')
    .delete()
    .in('correlativo', correlativos);
  if (error) throw error;
}

// ═══════════════════════════════════════════════════════════════════════════════
// DISCOUNT LOG — bitácora de descuentos
//
// La bitácora NO se escribe desde acá. La llena el trigger `descuento_guardian`
// cuando cambia `records.descuento` (o el de un comprobante de crédito), que es
// el único lugar donde un descuento existe de verdad. Si el front insertara
// filas por su cuenta —como hacía `logDiscount` hasta el 18-sep-2026— la
// bitácora diría que hay un descuento y la caja seguiría cobrando completo.
// ═══════════════════════════════════════════════════════════════════════════════

// Descuento adicional desde Caja, con verificación real de identidad.
//
// El rótulo del modal promete que un QB o Admin autoriza: acá esa promesa se
// cumple contra Supabase Auth. Se abre un cliente APARTE (sin persistir sesión
// ni tocar el storage del principal) para que validar al autorizador no cierre
// la sesión de quien está en caja; con esa sesión se llama el RPC, así que la
// base ve al autorizador en `auth.uid()` y no una cadena escrita a mano.
// Se manda `pct` (porcentaje sobre lo que falta cobrar) o `monto` (quetzales),
// nunca los dos: la base rechaza la mezcla.
export async function aplicarDescuentoCaja({ correlativo, pct = null, monto = null, motivo, email, password, solicitadoPor = null }) {
  const verificador = createClient(supabaseUrl, supabaseKey, {
    auth: { persistSession: false, autoRefreshToken: false, storageKey: 'innovalab-autorizador' },
  });

  try {
    const { error: eAuth } = await verificador.auth.signInWithPassword({
      email: (email || '').trim().toLowerCase(),
      password,
    });
    if (eAuth) throw new Error('Usuario o contraseña incorrectos.');

    const { data, error } = await verificador.rpc('aplicar_descuento_caja', {
      p_correlativo:    correlativo,
      p_pct:            monto != null ? null : pct,
      p_monto:          monto != null ? monto : null,
      p_motivo:         motivo || null,
      p_solicitado_por: solicitadoPor,
    });
    if (error) {
      // Los errores del RPC vienen como 'clave: explicación'; la clave no le
      // sirve a quien está en caja.
      const msg = String(error.message || error);
      throw new Error(msg.includes(':') ? msg.split(':').slice(1).join(':').trim() : msg);
    }
    return data;
  } finally {
    // Pase lo que pase, la sesión prestada no queda abierta.
    try { await verificador.auth.signOut({ scope: 'local' }); } catch {}
  }
}

export async function getDiscountLog() {
  const { data, error } = await supabase
    .from('discount_log')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) { console.error('getDiscountLog error:', error); return []; }
  return data || [];
}

// ═══════════════════════════════════════════════════════════════════════════════
// EXAM TEMPLATES — plantillas de parámetros y rangos de referencia
//
// Tabla: exam_templates
// Columnas:
//   id          uuid (PK, gen_random_uuid())
//   exam_id     text  — referencia al id del CATALOG (por convención, no FK estricto)
//   exam_name   text  — nombre legible del examen
//   categoria   text  — categoría del examen (ej: "Hematología")
//   secciones   jsonb — estructura completa:
//     [
//       {
//         nombre: "Serie Roja",
//         params: [
//           {
//             key: "hemoglobina",
//             label: "Hemoglobina",
//             unidad: "g/dL",
//             tipo: "numerico" | "opciones" | "texto_libre",
//             opciones: ["Positivo","Negativo"],   // solo si tipo=opciones
//             rangos: [
//               { sexo: "M"|"F"|"Ambos", edad_min: 0, edad_max: 999,
//                 min: 13.5, max: 17.5, valor_texto: null }
//             ]
//           }
//         ]
//       }
//     ]
//   created_at  timestamptz
//   updated_at  timestamptz  (actualizado automáticamente por trigger)
// ═══════════════════════════════════════════════════════════════════════════════

// Obtiene todas las plantillas (para precarga en el componente de catálogo)
export async function getExamTemplates() {
  const { data, error } = await supabase
    .from('exam_templates')
    .select('*')
    .order('exam_name', { ascending: true });
  if (error) { console.error('getExamTemplates error:', error); return []; }
  return data || [];
}

// Obtiene la plantilla de un examen específico.
// Busca primero por exam_id (id numérico del catálogo).
// Si no encuentra, busca por exam_name (fallback para plantillas migradas desde EXAM_PARAMS).
export async function getExamTemplate(examId, examName) {
  // Intento 1: por exam_id numérico
  const { data, error } = await supabase
    .from('exam_templates')
    .select('*')
    .eq('exam_id', String(examId))
    .single();
  if (!error && data) return data;

  // Intento 2: por exam_name (plantillas migradas desde refParams.js)
  if (examName) {
    const { data: dataByName, error: errByName } = await supabase
      .from('exam_templates')
      .select('*')
      .eq('exam_name', examName)
      .single();
    if (!errByName && dataByName) return dataByName;
  }

  return null;
}

// Trae en DOS consultas en paralelo (no una por examen) las plantillas de
// todos los exámenes de una orden. Devuelve una función síncrona
// (examId, examName) => plantilla|null con la MISMA prioridad que
// getExamTemplate: primero exam_id, luego exam_name. Si la carga en bloque
// falla, cae al camino viejo (una consulta por examen) para no romper el ingreso.
export async function getExamTemplatesLookup(examenes) {
  const ids = new Set();
  const nombres = new Set();
  for (const { examId, nombre } of examenes) {
    if (examId != null && examId !== '') ids.add(String(examId));
    if (nombre) { nombres.add(nombre); ids.add(nombre); } // getExamTemplate(examId ?? nombre)
  }
  try {
    const [porId, porNombre] = await Promise.all([
      ids.size     ? supabase.from('exam_templates').select('*').in('exam_id',   [...ids])     : { data: [] },
      nombres.size ? supabase.from('exam_templates').select('*').in('exam_name', [...nombres]) : { data: [] },
    ]);
    if (porId.error || porNombre.error) throw (porId.error || porNombre.error);
    const byId = new Map();
    const byName = new Map();
    for (const t of (porId.data || []))     if (!byId.has(String(t.exam_id))) byId.set(String(t.exam_id), t);
    for (const t of (porNombre.data || [])) if (!byName.has(t.exam_name))    byName.set(t.exam_name, t);
    return (examId, examName) =>
      byId.get(String(examId)) || (examName ? byName.get(examName) : null) || null;
  } catch (err) {
    console.warn('[getExamTemplatesLookup] carga en bloque falló, uso consulta por examen:', err?.message);
    const cache = new Map();
    await Promise.all(examenes.map(async ({ examId, nombre }) => {
      const k = `${examId ?? nombre}|${nombre}`;
      cache.set(k, await getExamTemplate(examId ?? nombre, nombre));
    }));
    return (examId, examName) => cache.get(`${examId}|${examName}`) ?? null;
  }
}

// ─── Resultado parcial (oct-2026) ───────────────────────────────────────────
// Última versión aprobada (snapshot inmutable que escribe el trigger al aprobar).
export async function getUltimaVersionResultado(correlativo) {
  const { data, error } = await supabase
    .from('result_versiones')
    .select('version,es_parcial,pendientes,valores,aprobado_at,qb_nombre')
    .eq('correlativo', correlativo)
    .order('version', { ascending: false })
    .limit(1);
  if (error) { console.error('getUltimaVersionResultado:', error); return null; }
  return data?.[0] || null;
}

// ─── Responsable de la muestra (oct-2026) ───────────────────────────────────
// Lista del personal activo que puede tomar muestras (op/qb/admin, sin la
// cuenta genérica). RPC security definer: el operador no puede leer perfiles
// ajenos por RLS, así que la lista sale de la base ya filtrada.
export async function getPersonalTomaMuestra() {
  const { data, error } = await supabase.rpc('personal_toma_muestra');
  if (error) { console.error('getPersonalTomaMuestra:', error); return []; }
  return data || [];
}

// Única vía para corregir el responsable de una orden ya guardada. La base
// exige motivo, bloquea si el resultado ya se envió a QB (salvo Admin) y deja
// el cambio en muestra_responsable_bitacora. Devuelve el jsonb `muestra` nuevo.
export async function corregirResponsableMuestra({ correlativo, tipo, responsableId, externo, motivo }) {
  const { data, error } = await supabase.rpc('corregir_responsable_muestra', {
    p_correlativo: correlativo, p_tipo: tipo,
    p_responsable_id: responsableId || null, p_externo: externo || null, p_motivo: motivo,
  });
  if (error) throw error;
  return data;
}

// Mensaje legible para los errores del guardián de la base.
export function msgResponsableMuestra(err) {
  const m = err?.message || String(err || '');
  return m.startsWith('responsable_muestra: ') ? m.slice('responsable_muestra: '.length) : m;
}

// Crea o actualiza la plantilla de un examen.
// Busca primero por exam_id numérico, luego por exam_name.
// Cuando guarda desde el editor del catálogo, siempre escribe el exam_id numérico correcto,
// corrigiendo así cualquier plantilla migrada que solo tenía el nombre como exam_id.
export async function upsertExamTemplate(template) {
  const payload = {
    exam_id:   String(template.exam_id),
    exam_name: template.exam_name,
    categoria: template.categoria || null,
    secciones: template.secciones || [],
  };

  // Buscar registro existente (por id numérico o por nombre)
  const existing = await getExamTemplate(template.exam_id, template.exam_name);

  if (existing) {
    const { data, error } = await supabase
      .from('exam_templates')
      .update(payload)
      .eq('id', existing.id)
      .select();
    if (error) throw error;
    return data[0];
  } else {
    const { data, error } = await supabase
      .from('exam_templates')
      .insert(payload)
      .select();
    if (error) throw error;
    return data[0];
  }
}

// Elimina la plantilla de un examen (no elimina el examen del catálogo)
export async function deleteExamTemplate(examId) {
  const { error } = await supabase
    .from('exam_templates')
    .delete()
    .eq('exam_id', String(examId));
  if (error) throw error;
}

// ═══════════════════════════════════════════════════════════════════════════════
// DISTRIBUTION LOG — Fix C
//
// Tabla: distribution_log
// Columnas:
//   id           uuid (PK, gen_random_uuid())
//   correlativo  text          — correlativo del resultado
//   metodo       text          — 'pdf' | 'whatsapp' | 'correo'
//   usuario      text          — username del que distribuyó
//   destinatario text nullable — email o teléfono (para correo/whatsapp)
//   created_at   timestamptz   default now()
//
// SQL para crear la tabla:
//   create table distribution_log (
//     id           uuid primary key default gen_random_uuid(),
//     correlativo  text not null,
//     metodo       text not null,
//     usuario      text not null,
//     destinatario text,
//     created_at   timestamptz default now()
//   );
//   alter table distribution_log enable row level security;
//   create policy "all_access" on distribution_log for all using (true) with check (true);
// ═══════════════════════════════════════════════════════════════════════════════

// Registra un evento de distribución
export async function logDistribution({ correlativo, metodo, usuario, destinatario = null, version = null }) {
  const { error } = await supabase
    .from('distribution_logs')
    .insert({ correlativo, metodo, enviado_a: destinatario, enviado_por: usuario, version });
  if (error) console.error('logDistribution error:', error);
}

// Obtiene el historial de distribución de un correlativo
export async function getDistributionLog(correlativo) {
  const { data, error } = await supabase
    .from('distribution_logs')
    .select('*')
    .eq('correlativo', correlativo)
    .order('created_at', { ascending: false });
  if (error) { console.error('getDistributionLog error:', error); return []; }
  return data || [];
}

// Obtiene el log completo (para precarga masiva en la vista de completados)
export async function getAllDistributionLogs() {
  const { data, error } = await supabase
    .from('distribution_logs')
    .select('correlativo, metodo, enviado_por, created_at')
    .order('created_at', { ascending: false });
  if (error) { console.error('getAllDistributionLogs error:', error); return []; }
  return data || [];
}

// ═══════════════════════════════════════════════════════════════════════════════
// QB PROFILE — firma, sello, no. colegiado
//
// Vive en tabla `profiles` (id = auth.uid()). Columnas: firma_url, sello_url,
// no_colegiado. Lectura: getQBProfile (self-read RLS). Escritura: saveQBProfile
// vía RPC SECURITY DEFINER `update_my_qb_profile` (ver migración SQL del RPC),
// que limita la escritura a esas 3 columnas y evita escalada de privilegios.
// La firma/sello se congelan en `results` al aprobar (inmutabilidad), no se leen
// en vivo desde profiles al generar el PDF.
// ═══════════════════════════════════════════════════════════════════════════════

// Sube imagen de sello al bucket 'signatures' y retorna la URL pública
export async function uploadQBSello(userId, file) {
  const path = `sello_${userId}_${Date.now()}.png`;
  const { error: upErr } = await supabase.storage
    .from('signatures')
    .upload(path, file, { upsert: true, contentType: file.type });
  if (upErr) throw upErr;

  const { data } = supabase.storage
    .from('signatures')
    .getPublicUrl(path);
  return data.publicUrl;
}

// Obtiene perfil QB completo desde profiles (id = auth.uid()). Self-read por RLS.
// La columna de nombre en profiles es `nombre`; se devuelve aliased como `name`
// para no cambiar a los consumidores (TabConfiguracion, TabQBResultados leen p.name).
export async function getQBProfile(userId) {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, name:nombre, no_colegiado, firma_url, sello_url, sede_id')
    .eq('id', userId)
    .single();
  if (error) { console.error('getQBProfile error:', error); return null; }
  return data;
}

// Guarda no_colegiado, firma_url y/o sello_url del usuario autenticado.
// Escribe vía RPC SECURITY DEFINER `update_my_qb_profile`, que SOLO toca esas 3
// columnas sobre la fila de auth.uid() — no permite modificar role/activo/etc.
// El parámetro userId se ignora a propósito: el RPC usa auth.uid() internamente
// (es siempre self-edit; el panel admin para editar a terceros es Fase 4/5).
export async function saveQBProfile(userId, updates) {
  // updates puede tener: { no_colegiado, firma_url, sello_url }
  // undefined => null => el RPC preserva el valor actual (COALESCE).
  const { error } = await supabase.rpc('update_my_qb_profile', {
    p_firma_url:    updates.firma_url    !== undefined ? updates.firma_url    : null,
    p_sello_url:    updates.sello_url    !== undefined ? updates.sello_url    : null,
    p_no_colegiado: updates.no_colegiado !== undefined ? updates.no_colegiado : null,
  });
  if (error) throw error;
}
// Obtiene el perfil del médico autenticado desde profiles. Self-read por RLS.
// Devuelve `nombre` + `no_colegiado` (poblados en Fase 1, NO editables aquí) y las
// 4 columnas editables del informe (especialidad, direccion, telefono, correo_contacto).
// Sin parámetros: resuelve auth.uid() internamente (siempre self-read, igual que el RPC).
export async function getMiPerfilMedico() {
  const { data: auth, error: authErr } = await supabase.auth.getUser();
  if (authErr || !auth?.user) { console.error('getMiPerfilMedico auth error:', authErr); return null; }
  const { data, error } = await supabase
    .from('profiles')
    .select('id, nombre, no_colegiado, especialidad, direccion, telefono, correo_contacto')
    .eq('id', auth.user.id)
    .single();
  if (error) { console.error('getMiPerfilMedico error:', error); return null; }
  return data;
}

// Actualiza las 4 columnas editables del perfil del médico vía RPC SECURITY DEFINER
// `update_my_medico_profile`, que escribe sobre la fila de auth.uid().
// CRÍTICO: el RPC NO usa COALESCE → se envían SIEMPRE los 4 parámetros completos.
// El editor del workspace precarga el perfil actual para no pisar columnas con null.
export async function updateMiPerfilMedico({ especialidad, direccion, telefono, correo_contacto }) {
  const { error } = await supabase.rpc('update_my_medico_profile', {
    p_especialidad:    especialidad    ?? null,
    p_direccion:       direccion       ?? null,
    p_telefono:        telefono        ?? null,
    p_correo_contacto: correo_contacto ?? null,
  });
  if (error) throw error;
}
// ═══════════════════════════════════════════════════════════════════════════════
// REALTIME HELPERS — sin cambios en la lógica, se usan igual que antes
// ═══════════════════════════════════════════════════════════════════════════════
// Estos canales ya se gestionan directamente en App.js con supabase.channel().
// No hay cambios aquí — se exporta supabase para que App.js lo use igual.

// ═══════════════════════════════════════════════════════════════════════════════
// PACIENTES — identidad estable (Paso 3). DPI = clave de negocio cuando existe.
// El record sigue guardando paciente (jsonb snapshot) + paciente_id (FK).
// ═══════════════════════════════════════════════════════════════════════════════

/** Normaliza DPI: deja solo dígitos. '' / null / sin dígitos -> '' */
export function normalizarDpi(raw) {
  if (raw == null) return '';
  return String(raw).replace(/\D/g, '');
}

/** ¿DPI guatemalteco bien formado? (exactamente 13 dígitos) */
export function dpiEsValido(dpiNorm) {
  return /^[0-9]{13}$/.test(dpiNorm);
}

/** Busca un paciente por DPI (normalizado). Devuelve la fila o null. */
export async function buscarPacientePorDpi(dpiRaw) {
  const dpi = normalizarDpi(dpiRaw);
  if (!dpiEsValido(dpi)) return null;
  const { data, error } = await supabase
    .from('pacientes')
    .select('*')
    .eq('dpi', dpi)
    .maybeSingle();
  if (error) throw error;
  return data || null;
}

/**
 * 4.1C — Reconciliación de identidad: enlaza expediente del lab (pacientes)
 * con ficha(s) del médico (med_pacientes). SECURITY DEFINER del lado servidor.
 *  - solicitudId presente  → ruta preorden (cadena de custodia).
 *  - solo medicoCodigo     → match por (médico, DPI) del expediente del lab.
 * Best-effort: el caller decide si el error bloquea (en saveOrder NO bloquea).
 * Devuelve el jsonb del RPC: { estado, via?, detalle? }.
 */
export async function vincularPacienteLab({ pacienteId, solicitudId = null, medicoCodigo = null }) {
  const { data, error } = await supabase.rpc('vincular_paciente_lab', {
    p_paciente_id:   pacienteId,
    p_solicitud_id:  solicitudId,
    p_medico_codigo: medicoCodigo || null,
  });
  if (error) throw error;
  return data;
}

/**
 * Resuelve-o-crea el paciente para una orden y devuelve su id (uuid).
 *  - DPI válido (13 díg) existente -> usa ese id. Si `actualizar`, refresca SOLO
 *    los campos no vacíos del form (nunca borra un dato bueno con uno vacío).
 *  - DPI válido nuevo -> inserta paciente con ese dpi.
 *  - Sin DPI / DPI irregular -> inserta paciente nuevo (dpi=null), 1 por orden.
 *    (Conservador: la fusión de duplicados sin DPI la hace un humano en la UI.)
 *
 * pac = objeto del form (nombre, apellido, nacimiento, sexo, telefono, nit, dpi)
 * Devuelve { id, creado: bool, actualizado: bool }
 */
export async function resolverOCrearPaciente(pac, { actualizar = true } = {}) {
  const limpio = (v) => {
    if (v == null) return null;
    const t = String(v).trim().replace(/\s+/g, ' ');
    return t === '' ? null : t;
  };
  const dpiNorm = normalizarDpi(pac?.dpi);
  const dpiOk   = dpiEsValido(dpiNorm);

  const baseFila = {
    nombre:     limpio(pac?.nombre) || '(sin nombre)',
    apellido:   limpio(pac?.apellido),
    nacimiento: limpio(pac?.nacimiento), // 'YYYY-MM-DD' o null
    sexo:       limpio(pac?.sexo),
    telefono:   limpio(pac?.telefono),
    nit:        limpio(pac?.nit),
  };

  // 1) DPI válido: buscar existente
  if (dpiOk) {
    const existente = await buscarPacientePorDpi(dpiNorm);
    if (existente) {
      let actualizado = false;
      if (actualizar) {
        // Solo campos con valor; un vacío del form NO sobreescribe un dato bueno.
        const patch = {};
        for (const k of ['nombre', 'apellido', 'nacimiento', 'sexo', 'telefono', 'nit']) {
          if (baseFila[k] != null && baseFila[k] !== existente[k]) patch[k] = baseFila[k];
        }
        if (Object.keys(patch).length) {
          const { error } = await supabase
            .from('pacientes')
            .update(patch)
            .eq('id', existente.id);
          if (error) throw error;
          actualizado = true;
        }
      }
      return { id: existente.id, creado: false, actualizado };
    }
    // No existe -> crear con DPI
    const { data, error } = await supabase
      .from('pacientes')
      .insert({ dpi: dpiNorm, dpi_revisar: false, ...baseFila })
      .select('id')
      .single();
    if (error) throw error;
    return { id: data.id, creado: true, actualizado: false };
  }

  // 2) Sin DPI o DPI irregular -> paciente nuevo (dpi null). dpi_revisar si había algo no vacío.
  const teniaAlgo = normalizarDpi(pac?.dpi) !== '' && !dpiOk;
  const { data, error } = await supabase
    .from('pacientes')
    .insert({ dpi: null, dpi_revisar: teniaAlgo, ...baseFila })
    .select('id')
    .single();
  if (error) throw error;
  return { id: data.id, creado: true, actualizado: false };
}

// ═══════════════════════════════════════════════════════════════════════════════
// PACIENTES DEL MÉDICO — expediente clínico (Paso 3)
//
// BD del médico, separada de `pacientes` (lab). RLS: el médico es dueño de SUS
// filas. El `medico_codigo` lo sella un trigger en el insert → acá NO se pasa.
// El enlace con resultados de lab se resuelve por DPI (auto) o lab_paciente_id
// (manual). El acceso a resultados sigue atado a la referencia (RLS de records),
// el feed solo agrupa: ver get_expediente_resultados (RPC SECURITY DEFINER).
// ═══════════════════════════════════════════════════════════════════════════════

/** Lista los pacientes del médico autenticado (RLS los acota a él). */
export async function getMedPacientes() {
  const { data, error } = await supabase
    .from('med_pacientes')
    .select('*')
    .order('nombre', { ascending: true });
  if (error) { console.error('getMedPacientes error:', error); return []; }
  return data || [];
}
// Alias de compatibilidad: TabWorkspaceMedico.js importa `getMisMedPacientes`,
// pero el helper canónico se llama `getMedPacientes`. Mismo comportamiento (lista
// los pacientes del médico autenticado, acotados por RLS) y misma firma (sin args).
// Se mantiene el alias para no tocar el workspace; si algún día se unifica el nombre,
// renombrar el import en TabWorkspaceMedico.js y borrar esta línea.
export const getMisMedPacientes = getMedPacientes;
/** Busca en el roster del médico un paciente por DPI (normalizado). Fila o null. */
export async function buscarMedPacientePorDpi(dpiRaw, medicoCodigo = null) {
  const dpi = normalizarDpi(dpiRaw);
  if (!dpiEsValido(dpi)) return null;
  let q = supabase.from('med_pacientes').select('*').eq('dpi', dpi);
  // Una secretaria ve el roster de VARIOS doctores: sin este filtro, el DPI de un
  // paciente del doctor A haría "match" al dar de alta en la agenda del doctor B.
  if (medicoCodigo) q = q.eq('medico_codigo', medicoCodigo);
  const { data, error } = await q.maybeSingle();
  if (error) throw error;
  return data || null;
}

/**
 * Alta de paciente desde la agenda, para un doctor concreto.
 *
 * Es la única vía válida cuando quien agenda es una secretaria: el insert
 * directo en `med_pacientes` exige un `medico_codigo` que ella no puede sellar,
 * y el trigger lo rechaza. El RPC valida el permiso sobre ese doctor y deduplica
 * por DPI del lado del servidor.
 */
export async function crearPacienteAgenda({ medicoCodigo, nombre, apellido, telefono, nacimiento, sexo, dpi, notas, codigoClinica, email, direccion, verificarDuplicados = false, descartados = null }) {
  const { data, error } = await supabase.rpc('crear_paciente_agenda', {
    p_medico_codigo: medicoCodigo,
    p_nombre: (nombre || '').trim() || '(sin nombre)',
    p_apellido: apellido?.trim() || null,
    p_telefono: telefono?.trim() || null,
    p_nacimiento: nacimiento || null,
    p_sexo: sexo || null,
    p_dpi: dpi?.trim() || null,
    p_notas: notas?.trim() || null,
    p_codigo_clinica: codigoClinica?.trim() || null,
    p_email: email?.trim() || null,
    p_direccion: direccion?.trim() || null,
    // La base frena el alta si hay una ficha parecida (nombre, teléfono) y
    // la pantalla no la descartó explícitamente con "es otra persona".
    p_verificar_duplicados: !!verificarDuplicados,
    p_descartados: descartados && descartados.length ? descartados : null,
  });
  if (error) throw error;
  return Array.isArray(data) ? data[0] : data;
}

/**
 * Fichas del padrón del médico que podrían ser la misma persona: nombre
 * parecido (sin tildes ni mayúsculas), mismo teléfono o mismo DPI. Hasta 5,
 * con la fecha de su última cita para distinguir homónimos.
 */
export async function buscarPosiblesDuplicados({ medicoCodigo, nombre, apellido, telefono, dpi }) {
  const { data, error } = await supabase.rpc('buscar_posibles_duplicados', {
    p_medico_codigo: medicoCodigo,
    p_nombre: nombre?.trim() || '',
    p_apellido: apellido?.trim() || null,
    p_telefono: telefono?.trim() || null,
    p_dpi: dpi?.trim() || null,
  });
  if (error) throw error;
  return data || [];
}

/**
 * Edita los datos de identificación de una ficha, incluido el código de
 * expediente de la clínica. La base rechaza un código repetido dentro de la
 * misma institución y devuelve `codigo_duplicado` con el nombre de quien lo
 * tiene — `agendaErrores.js` lo traduce.
 */
export async function actualizarPacienteAgenda(pacienteId, { nombre, apellido, telefono, nacimiento, sexo, dpi, codigoClinica, email, direccion } = {}) {
  const { data, error } = await supabase.rpc('actualizar_paciente_agenda', {
    p_paciente_id: pacienteId,
    p_nombre: nombre?.trim() || null,
    p_apellido: apellido?.trim() || null,
    p_telefono: telefono?.trim() || null,
    p_nacimiento: nacimiento || null,
    p_sexo: sexo || null,
    p_dpi: dpi?.trim() || null,
    p_codigo_clinica: codigoClinica?.trim() || null,
    p_email: email?.trim() || null,
    p_direccion: direccion?.trim() || null,
  });
  if (error) throw error;
  return Array.isArray(data) ? data[0] : data;
}

/** Crea un paciente del médico. No se pasa medico_codigo (lo sella el trigger). */
export async function crearMedPaciente(pac) {
  const dpiNorm = normalizarDpi(pac?.dpi);
  const fila = {
    nombre:     (pac?.nombre || '').trim() || '(sin nombre)',
    apellido:   pac?.apellido?.trim()   || null,
    nacimiento: pac?.nacimiento || null,
    sexo:       pac?.sexo       || null,
    telefono:   pac?.telefono?.trim()   || null,
    dpi:        dpiEsValido(dpiNorm) ? dpiNorm : null,
    notas:      pac?.notas?.trim()      || null,
    // El código de expediente de la clínica, cuando la pantalla lo ofrece. El
    // trigger lo normaliza (trim, vacío → null) y el índice único lo protege.
    codigo_clinica: pac?.codigo_clinica?.trim() || null,
  };
  const { data, error } = await supabase
    .from('med_pacientes')
    .insert(fila)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

/** Actualiza campos de un paciente del médico. `patch` con las columnas a tocar. */
export async function actualizarMedPaciente(id, patch) {
  const limpio = { ...patch, updated_at: new Date().toISOString() };
  if ('dpi' in limpio) {
    const d = normalizarDpi(limpio.dpi);
    limpio.dpi = dpiEsValido(d) ? d : null;
  }
  const { data, error } = await supabase
    .from('med_pacientes')
    .update(limpio)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

export async function eliminarMedPaciente(id) {
  const { error } = await supabase.from('med_pacientes').delete().eq('id', id);
  if (error) throw error;
}

/** Reasignación manual (regla 3): enlaza este paciente a un paciente de lab.
 *  4.1C: se declara la vía y se apaga la bandera de revisión — la reasignación
 *  del médico es deliberada, no necesita revisarse a sí misma. El quién/cuándo
 *  lo estampa el trigger trg_med_pacientes_vinculo en la base. */
export async function reasignarLabPaciente(id, labPacienteId) {
  return actualizarMedPaciente(id, {
    lab_paciente_id:     labPacienteId,
    lab_vinculo_via:     'manual',
    lab_vinculo_revisar: false,
    lab_vinculo_nota:    null,
  });
}

/** Corrección: quita el vínculo de lab de una ficha (queda sin expediente de
 *  laboratorio). Se usa al MOVER un resultado a otra ficha, para no dejar dos
 *  fichas apuntando al mismo expediente (integridad del puente lab_paciente_id). */
export async function desvincularLabPaciente(id) {
  return actualizarMedPaciente(id, {
    lab_paciente_id:     null,
    lab_vinculo_via:     null,
    lab_vinculo_revisar: false,
    lab_vinculo_nota:    null,
  });
}

/** Feed de resultados del expediente (solo órdenes que ESTE médico refirió).
 *
 *  El RPC acepta ventana de fechas y tope; los parámetros opcionales se OMITEN
 *  cuando no se piden, para que la función use sus valores por defecto (sin
 *  ventana, 50 resultados). Así una llamada sin opciones se comporta igual que
 *  antes desde el punto de vista del que llama — es la que usa ai-assistant.
 *
 *  @param {string} medPacienteId
 *  @param {{desde?: string, hasta?: string, limite?: number, offset?: number}} [opciones]
 *         desde/hasta en 'YYYY-MM-DD', interpretados en hora de Guatemala por
 *         el servidor (public.rango_local). limite se acota a [1, 200] allá.
 *  Cada fila trae además `total` (cuántos hay en la ventana, sin el tope) y
 *  `primera_fecha` (el más antiguo del paciente, ignorando la ventana).
 */
export async function getExpedienteResultados(medPacienteId, opciones = {}) {
  const { desde, hasta, limite, offset } = opciones;
  const args = { p_med_paciente_id: medPacienteId };
  if (desde)          args.p_desde  = desde;
  if (hasta)          args.p_hasta  = hasta;
  if (limite != null) args.p_limite = limite;
  if (offset != null) args.p_offset = offset;
  const { data, error } = await supabase
    .rpc('get_expediente_resultados', args);
  if (error) { console.error('getExpedienteResultados error:', error); return []; }
  return data || [];
}

// ═══════════════════════════════════════════════════════════════════
// VÍNCULO DE EXPEDIENTES — SOLO CONSULTA.
// Cada expediente sigue siendo individual: sus datos, sus laboratorios, sus PDFs.
// El grupo únicamente permite abrir los expedientes hermanos desde la cabecera.
// Admite N miembros: pareja, o paciente pediátrico + madre + padre.
// La escritura va por RPC (SECURITY DEFINER); las tablas no aceptan INSERT directo.
// ═══════════════════════════════════════════════════════════════════

/** Grupos activos del expediente. [] si no está vinculado. */
export async function getExpedienteVinculos(medPacienteId) {
  if (!medPacienteId) return [];
  const { data, error } = await supabase
    .rpc('get_expediente_vinculos', { p_med_paciente_id: medPacienteId });
  if (error) { console.error('getExpedienteVinculos error:', error); return []; }
  return data || [];
}

/** Para pintar la pastilla en un listado sin una llamada por paciente. */
export async function getVinculosResumen(ids = []) {
  if (!ids || !ids.length) return {};
  const { data, error } = await supabase.rpc('get_vinculos_resumen', { p_ids: ids });
  if (error) { console.error('getVinculosResumen error:', error); return {}; }
  return data || {};
}

/** Vincula 2 o más expedientes. Si alguno ya tiene grupo del mismo tipo, crece ese. */
export async function vincularExpedientes(miembros = [], opciones = {}) {
  const { tipo = 'pareja', roles = null, etiqueta = null } = opciones;
  const { data, error } = await supabase.rpc('vincular_expedientes', {
    p_miembros: miembros,
    p_tipo: tipo,
    p_roles: roles,
    p_etiqueta: etiqueta,
  });
  if (error) throw error;
  return data;
}

/**
 * No borra: cierra la membresía con fecha. El expediente queda intacto.
 * notas: 'ambos'  → las notas conjuntas siguen visibles en los dos expedientes
 *        'origen' → cada nota queda solo en el expediente donde se escribió
 */
export async function desvincularExpediente(grupoId, medPacienteId, nota = null, notas = 'ambos') {
  const { data, error } = await supabase.rpc('desvincular_expediente', {
    p_grupo_id: grupoId,
    p_med_paciente_id: medPacienteId,
    p_nota: nota,
    p_notas: notas,
  });
  if (error) throw error;
  return data;
}
// ═══════════════════════════════════════════════════════════════════
// CITAS — agenda del médico (Paso 3). medico_codigo lo estampa el trigger,
// nunca se manda desde el cliente. RLS deja ver/gestionar solo las propias.
// ═══════════════════════════════════════════════════════════════════
export async function getCitas(medPacienteId) {
  const { data, error } = await supabase
    .from('citas')
    .select('*')
    .eq('med_paciente_id', medPacienteId)
    .order('inicio', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function crearCita(cita) {
  const { data, error } = await supabase
    .from('citas')
    .insert(cita)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function actualizarCita(id, patch) {
  const { data, error } = await supabase
    .from('citas')
    .update(patch)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function eliminarCita(id) {
  const { error } = await supabase.from('citas').delete().eq('id', id);
  if (error) throw error;
}
/**
 * Indicadores de la CLINICA del medico, no del laboratorio.
 *
 * El RPC get_med_dashboard resuelve la relacion del medico con Innovalab
 * (examenes referidos, resultados listos, comision). Eso importa, pero no es
 * lo que el medico viene a ver al abrir su portal: un medico de clinica sin
 * laboratorio propio encontraba las cuatro tarjetas en cero teniendo la agenda
 * llena. Estas tres cuentas cubren lo suyo.
 *
 * Se piden con head:true y count:'exact': el servidor devuelve solo el numero,
 * ninguna fila viaja. Tres consultas de conteo pesan menos que una sola que
 * traiga los registros para contarlos en el navegador.
 */
export async function getMedResumenClinica() {
  const tz    = 'America/Guatemala';
  const ahora = new Date();
  const hoy   = new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(ahora); // YYYY-MM-DD
  const iniMes  = `${hoy.slice(0, 7)}-01T00:00:00-06:00`;
  const iniDia  = `${hoy}T00:00:00-06:00`;
  const finDia  = `${hoy}T23:59:59-06:00`;

  const [pac, cons, citasHoy] = await Promise.all([
    supabase.from('med_pacientes').select('id', { count: 'exact', head: true }),
    supabase.from('consultas').select('id', { count: 'exact', head: true })
      .eq('estado', 'cerrada').gte('fecha_cierre', iniMes),
    supabase.from('citas').select('id', { count: 'exact', head: true })
      .neq('estado', 'cancelada').gte('inicio', iniDia).lte('inicio', finDia),
  ]);

  for (const r of [pac, cons, citasHoy]) {
    if (r.error) { console.error('getMedResumenClinica:', r.error); throw r.error; }
  }
  return {
    pacientes:      pac.count      ?? 0,
    consultasMes:   cons.count     ?? 0,
    citasHoy:       citasHoy.count ?? 0,
  };
}

// Agenda del médico: todas SUS citas (RLS) con el nombre del paciente embebido.
// Pasa { desde } en ISO para traer solo de hoy en adelante.
export async function getAgenda({ desde = null, hasta = null, limite = 200 } = {}) {
  // `limite` no es opcional por capricho: sin tope, una clinica con dos anos de
  // uso devuelve miles de filas a una pantalla que solo necesita los proximos
  // dias. Quien necesite el calendario completo usa getAgendaRango (Mi Clinica).
  let q = supabase
    .from('citas')
    .select('*, med_pacientes(nombre, apellido, telefono)')
    .order('inicio', { ascending: true });
  if (desde) q = q.gte('inicio', desde);
  if (hasta) q = q.lte('inicio', hasta);
  if (limite) q = q.limit(limite);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

// ═══════════════════════════════════════════════════════════════════════════════
// AGENDA FASE 2 — horarios, bloqueos y disponibilidad
//
// Las reglas viven en la base, no acá: cupo simultáneo por doctor, validación
// de franja y bloqueo por trigger, y bitácora append-only. Estos
// helpers solo transportan datos. Si la base rechaza algo, el error sube tal
// cual y lo traduce `src/agendaErrores.js`.
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Doctores sobre los que el usuario puede actuar: el propio si es médico, los
 * asignados si es secretaria. Trae permisos por doctor (puede_agendar/cancelar).
 * RPC porque la política de `doctors` no incluye al rol secretaria — y ampliarla
 * le daría acceso a la ficha completa del doctor, que no necesita.
 */
export async function getMisDoctores() {
  const { data, error } = await supabase.rpc('get_mis_doctores');
  if (error) { console.error('getMisDoctores error:', error); throw error; }
  return data || [];
}

/**
 * Mapa `clave → boolean` de los permisos del usuario actual.
 *
 * Decide qué se MUESTRA, no qué se puede hacer: quien manda sigue siendo la RLS.
 * Lo resuelve la misma función que usan las policies (`tiene_permiso`), así que
 * la pantalla y la base nunca discrepan — un botón que se ve, funciona.
 */
export async function getMisPermisos() {
  const { data, error } = await supabase.rpc('get_mis_permisos');
  if (error) { console.error('getMisPermisos error:', error); throw error; }
  return data || {};
}

// ═══════════════════════════════════════════════════════════════════
// SECRETARIA · resultados, informes y solicitudes del paciente (03-oct-2026)
// Dos llaves, ambas del laboratorio: el médico comparte
// (doctor_comparte_documentos) y la secretaria tiene el permiso
// expediente.resultados_lab.ver / expediente.documentos_medico.ver. Si falta
// cualquiera, los RPC rebotan. Las tablas clínicas siguen cerradas.
// ═══════════════════════════════════════════════════════════════════

/** Resultados APROBADOS del paciente. Mismo shape que get_expediente_resultados. */
export async function getResultadosSecretaria(medPacienteId, { limite = 25, offset = 0 } = {}) {
  const { data, error } = await supabase.rpc('get_resultados_secretaria', {
    p_med_paciente_id: medPacienteId, p_limite: limite, p_offset: offset,
  });
  if (error) throw error;
  return data || [];
}

/** { perfil_medico, consultas: [{ id, fecha, receta|null, solicitud|null }] } */
export async function getDocumentosSecretaria(medPacienteId) {
  const { data, error } = await supabase.rpc('get_documentos_secretaria', {
    p_med_paciente_id: medPacienteId,
  });
  if (error) throw error;
  return data || { perfil_medico: null, consultas: [] };
}

/**
 * Deja la descarga en la bitácora. Se llama ANTES de generar el PDF: si el
 * registro falla, no hay documento. tipo: 'resultado' | 'informe' | 'solicitud'.
 */
export async function registrarDescargaSecretaria(medPacienteId, tipo, referencia) {
  const { error } = await supabase.rpc('registrar_descarga_secretaria', {
    p_med_paciente_id: medPacienteId, p_tipo: tipo, p_referencia: String(referencia),
  });
  if (error) throw error;
}

/** Solo admin: qué médicos comparten sus documentos con su secretaría. */
export async function getDoctoresCompartenDocumentos() {
  const { data, error } = await supabase.rpc('get_doctores_comparten_documentos');
  if (error) throw error;
  return data || [];
}

/** Solo admin: registra la decisión del médico (queda auditada). */
export async function setDoctorComparteDocumentos(doctorId, activo, motivo = null) {
  const { error } = await supabase.rpc('set_doctor_comparte_documentos', {
    p_doctor_id: doctorId, p_activo: activo, p_motivo: motivo,
  });
  if (error) throw error;
}

/**
 * Permisos de OTRO perfil, para administrarlos. Trae el catálogo que aplica a su
 * rol con el valor efectivo ya resuelto, y separa el override del default para
 * poder mostrar "esto viene de fábrica" vs "esto lo cambió alguien".
 *
 * Quién puede: admin sobre cualquiera; manager sobre la gente de su institución
 * (`puede_administrar_permisos` en la base). El RPC rebota si no.
 */
export async function getPermisosPerfil(perfilId) {
  if (!perfilId) return [];
  const { data, error } = await supabase.rpc('get_permisos_perfil', { p_perfil_id: perfilId });
  if (error) throw error;
  return data || [];
}

/**
 * Enciende, apaga o RESTAURA un permiso.
 *
 * `concedido = null` borra el override y devuelve la clave a su default. No es lo
 * mismo que apagarlo: si algún día cambia el default del catálogo, los usuarios
 * con override explícito se quedan clavados en el valor viejo, y los restaurados
 * siguen la nueva política.
 */
export async function setPermiso(perfilId, clave, concedido, motivo = null) {
  const { data, error } = await supabase.rpc('set_permiso', {
    p_perfil_id: perfilId, p_clave: clave, p_concedido: concedido, p_motivo: motivo,
  });
  if (error) throw error;
  return data;
}

/** Enciende o apaga un módulo entero (agenda, pacientes, archivos, dashboard). */
export async function setPermisosModulo(perfilId, modulo, concedido, motivo = null) {
  const { data, error } = await supabase.rpc('set_permisos_modulo', {
    p_perfil_id: perfilId, p_modulo: modulo, p_concedido: concedido, p_motivo: motivo,
  });
  if (error) throw error;
  return data;
}

/**
 * Dashboard de la secretaria: agenda de hoy, lo que viene y pendientes.
 *
 * No reusa `get_med_dashboard()` a propósito: ese calcula comisiones y producción
 * del mes apoyándose en el código de doctor, que para ella es NULL — le devolvía
 * todo en cero. Los recordatorios se derivan de la agenda, no hay tabla que
 * mantener. Trae también `permisos` por tarjeta, ya resueltos por el servidor.
 *
 * @param {number} [dias=7] - horizonte de "próximas citas" (1–30)
 */
export async function getSecDashboard(dias = 7) {
  const { data, error } = await supabase.rpc('get_sec_dashboard', { p_dias: dias });
  if (error) { console.error('getSecDashboard error:', error); throw error; }
  return data || null;
}

/**
 * Roster de un doctor para agendar (solo datos de identificación).
 *
 * No usa `getMedPacientes()` a propósito: `med_pacientes` solo abre al médico
 * dueño, así que a una secretaria la tabla le devuelve cero filas. El RPC valida
 * el permiso sobre el doctor y devuelve nombre, teléfono y DPI — nunca alergias,
 * datos críticos ni notas, que la fase 1 le cerró.
 */
export async function getPacientesAgenda(medicoCodigo) {
  if (!medicoCodigo) return [];
  const { data, error } = await supabase.rpc('get_pacientes_agenda', { p_medico_codigo: medicoCodigo });
  if (error) throw error;
  return data || [];
}

// Version paginada/buscable server-side (escala sin cargar todo el roster).
// p_medico_codigo null => usa el codigo de la sesion. Devuelve { rows, total }.
export async function getPacientesAgendaPag({ medicoCodigo = null, busqueda = '', inicial = '', incluirPrestados = false, limite = 10, offset = 0 } = {}) {
  const { data, error } = await supabase.rpc('get_pacientes_agenda_pag', {
    p_medico_codigo: medicoCodigo || null,
    p_busqueda: busqueda || null,
    p_inicial: inicial || null,
    p_limite: limite,
    p_offset: offset,
    p_incluir_prestados: incluirPrestados,
  });
  if (error) throw error;
  const rows = data || [];
  const total = rows.length ? Number(rows[0].total) : 0;
  return { rows, total };
}

/**
 * Citas de un doctor en un rango, con el nombre del paciente ya resuelto.
 *
 * El embed de PostgREST (`citas.select('*, med_pacientes(...)')`) devuelve null
 * en el join para una secretaria — misma causa que arriba — y el calendario le
 * mostraría "Cita" en vez del paciente. Se normaliza la forma anidada para que
 * el resto del componente no note la diferencia.
 */
export async function getAgendaRango({ medicoCodigo, desde = null, hasta = null }) {
  if (!medicoCodigo) return [];
  const { data, error } = await supabase.rpc('get_agenda_rango', {
    p_medico_codigo: medicoCodigo,
    p_desde: desde,
    p_hasta: hasta,
  });
  if (error) throw error;
  return (data || []).map((c) => ({
    ...c,
    med_pacientes: {
      nombre: c.paciente_nombre,
      apellido: c.paciente_apellido,
      telefono: c.paciente_telefono,
    },
  }));
}

/**
 * Agenda consolidada de TODA la institución, para recepción.
 *
 * Complementa a `getAgendaRango`, que es un médico a la vez. Acá vienen todos
 * los médicos de la institución en una sola consulta y con el detalle completo
 * de cada cita —paciente, teléfono, motivo y notas—, que es el mismo que ella
 * ya ve en la agenda 1 a 1: quien contesta el teléfono necesita reconocer al
 * paciente sin saber de antemano con qué doctor va.
 *
 * No es la misma RPC que la del manager (`getAgendaGerencia`): aquélla oculta
 * la identidad del paciente salvo permiso expreso y nunca manda las notas.
 *
 * Cada fila trae `asignado` (si el médico es de los suyos en
 * `secretaria_doctores` o llega por el alcance institucional) y los flags
 * `puede_editar` / `puede_cancelar`, ya resueltos por la base contra
 * `agenda.modificar` y `agenda.cancelar`.
 *
 * Requiere `agenda.institucion.ver`; sin esa llave el RPC rebota.
 */
export async function getAgendaInstitucion({ desde = null, hasta = null } = {}) {
  const { data, error } = await supabase.rpc('get_agenda_institucion', {
    p_desde: ymd(desde), p_hasta: ymd(hasta),
  });
  if (error) throw error;
  return data || [];
}

/** 'YYYY-MM-DD' a partir de un Date con hora-pared GT. */
function _fechaISO(d) {
  const p2 = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p2(d.getMonth() + 1)}-${p2(d.getDate())}`;
}

/**
 * Espacios libres reales de un doctor, calculados en la base:
 * franjas activas − bloqueos − citas ocupadas − horas ya pasadas.
 *
 * El frontend NO debe calcular disponibilidad por su cuenta: si lo hace, tarde o
 * temprano ofrece un espacio que la base va a rechazar, y la secretaria escribe
 * la cita completa antes de enterarse.
 *
 * @param {Object} opts
 * @param {string} opts.medicoCodigo
 * @param {Date|string} opts.desde  - Date (hora-pared GT) o 'YYYY-MM-DD'
 * @param {Date|string} opts.hasta
 * @param {number} [opts.duracionMin=30] - largo de la cita a encajar
 * @param {number} [opts.pasoMin]        - cada cuánto se ofrece un inicio (def: la duración)
 * @returns {Promise<Array<{inicio:string, fin:string, libres:number}>>} ISO UTC;
 *          `libres` = cuántos lugares quedan en ese espacio (cupo − citas ya puestas)
 */
export async function getSlotsDisponibles({ medicoCodigo, desde, hasta, duracionMin = 30, pasoMin = null }) {
  if (!medicoCodigo) return [];
  const { data, error } = await supabase.rpc('slots_disponibles', {
    p_medico_codigo: medicoCodigo,
    p_desde: typeof desde === 'string' ? desde : _fechaISO(desde),
    p_hasta: typeof hasta === 'string' ? hasta : _fechaISO(hasta),
    p_duracion_min: duracionMin,
    p_paso_min: pasoMin,
  });
  if (error) throw error;
  return data || [];
}

// ─── Admisión: formulario público ────────────────────────────────────────────
// Las dos únicas llamadas que hace la pantalla que ve el paciente. Corren SIN
// sesión (rol `anon`), así que del otro lado no hay tabla abierta: hay dos
// funciones, una que devuelve un formulario en blanco y otra que deja una
// respuesta en un buzón de una sola dirección. El link puede circular por
// WhatsApp sin que eso abra nada de la clínica.

export async function getAdmisionFormulario(token) {
  const { data, error } = await supabase.rpc('get_admision_formulario', { p_token: token });
  if (error) throw error;
  return data;
}

export async function enviarAdmision(token, datos) {
  const { data, error } = await supabase.rpc('enviar_admision', { p_token: token, p_datos: datos });
  if (error) throw error;
  return data;
}

// ─── Admisión: lado de la clínica ────────────────────────────────────────────
// Una respuesta no es un paciente: alguien la revisa, la corrige y recién ahí
// se crea la ficha. Ese paso intermedio es lo que hace que un link público
// repartido por WhatsApp no sea un riesgo.

export async function getAdmisionPlantillas(institucionId) {
  const { data, error } = await supabase.rpc('get_admision_plantillas', { p_institucion_id: institucionId });
  if (error) throw error;
  return data || [];
}

/** Guarda el formulario. La base valida el esquema y rechaza uno sin la pregunta
 *  del nombre: sin ella la bandeja no distingue una respuesta de otra y las
 *  fichas nacen como "(sin nombre)". El token NO cambia — los links repartidos
 *  siguen sirviendo. */
export async function guardarAdmisionPlantilla(id, nombre, esquema, activa = null) {
  const { data, error } = await supabase.rpc('guardar_admision_plantilla', {
    p_id: id, p_nombre: nombre, p_esquema: esquema, p_activa: activa,
  });
  if (error) throw error;
  return data;
}

export async function crearAdmisionPlantilla(institucionId, nombre = 'Hoja de datos') {
  const { data, error } = await supabase.rpc('crear_admision_plantilla', {
    p_institucion_id: institucionId, p_nombre: nombre,
  });
  if (error) throw error;
  return data;
}

/** Corta un enlace que se filtro o que esta recibiendo basura. Los que ya se
 *  repartieron dejan de servir: por eso es una accion aparte y no un efecto
 *  colateral de guardar. */
export async function rotarTokenAdmision(id) {
  const { data, error } = await supabase.rpc('rotar_token_admision', { p_id: id });
  if (error) throw error;
  return data;
}

export async function getAdmisionBandeja(institucionId, { estado = 'pendiente', limite = 100 } = {}) {
  const { data, error } = await supabase.rpc('get_admision_bandeja', {
    p_institucion_id: institucionId, p_estado: estado, p_limite: limite,
  });
  if (error) throw error;
  return data || [];
}

/** Las clínicas cuya bandeja puede ver el usuario. Se pregunta a la base y no
 *  se deduce del roster de doctores: esa adivinanza falla con la secretaria que
 *  atiende dos clínicas. */
export async function getMisClinicasAdmision() {
  const { data, error } = await supabase.rpc('get_mis_clinicas_admision');
  if (error) throw error;
  return data || [];
}

/** Arregla lo que el paciente escribió mal ANTES de que se vuelva una ficha
 *  clínica, que después es inmutable. Lo original queda guardado aparte. */
export async function corregirAdmision(id, datos) {
  const { data, error } = await supabase.rpc('corregir_admision', { p_id: id, p_datos: datos });
  if (error) throw error;
  return data;
}

// Edita una hoja YA ACEPTADA: guarda la hoja, lleva a la ficha lo que cambió
// (nombre, apellido, teléfono, nacimiento, DPI) y ajusta los códigos.
// Códigos: null = no tocar, '' = quitar, texto = asignar.
export async function editarAdmisionAceptada(id, datos, { codigo = null, codigoPareja = null } = {}) {
  const { data, error } = await supabase.rpc('editar_admision_aceptada', {
    p_id: id,
    p_datos: datos,
    p_codigo: codigo,
    p_codigo_pareja: codigoPareja,
  });
  if (error) throw error;
  return data;
}

// Hoja llenada en recepción (2026-09-28). Sin `medPacienteId`: crea la hoja y
// la acepta en el mismo paso (ficha nueva o reusada por DPI). Con
// `medPacienteId`: le pone hoja a un paciente que ya existía (creado desde la
// agenda) y le actualiza la ficha con lo que se escribió.
export async function crearAdmisionRecepcion(plantillaId, datos, { medicoCodigo = null, codigo = null, codigoPareja = null, medPacienteId = null } = {}) {
  const { data, error } = await supabase.rpc('crear_admision_recepcion', {
    p_plantilla_id: plantillaId,
    p_datos: datos,
    p_medico_codigo: medicoCodigo,
    p_codigo: codigo,
    p_codigo_pareja: codigoPareja,
    p_med_paciente_id: medPacienteId,
  });
  if (error) throw error;
  return data;
}

// Id de la hoja aceptada de una ficha, o null si no tiene.
export async function getAdmisionDeFicha(medPacienteId) {
  const { data, error } = await supabase.rpc('get_admision_de_ficha', { p_med_paciente_id: medPacienteId });
  if (error) throw error;
  return data || null;
}

export async function getAdmisionRespuesta(id) {
  const { data, error } = await supabase.rpc('get_admision_respuesta', { p_id: id });
  if (error) throw error;
  return data;
}

export async function aceptarAdmision(id, { medicoCodigo = null, codigoClinica = null, codigoClinicaPareja = null } = {}) {
  const { data, error } = await supabase.rpc('aceptar_admision', {
    p_id: id,
    p_medico_codigo: medicoCodigo,
    p_codigo_clinica: codigoClinica,
    p_codigo_clinica_pareja: codigoClinicaPareja,
  });
  if (error) throw error;
  return data;
}

export async function descartarAdmision(id, nota = null) {
  const { data, error } = await supabase.rpc('descartar_admision', { p_id: id, p_nota: nota });
  if (error) throw error;
  return data;
}

/**
 * Marca de la clínica a la que pertenece un doctor: nombre y logo.
 *
 * Los documentos que el paciente se lleva (receta, informe, solicitud, hoja de
 * admisión) son papelería de la CLÍNICA, no de Innovalab. Se pregunta por
 * doctor y no por usuario porque una secretaria puede atender doctores de dos
 * clínicas distintas y la respuesta correcta depende de en cuál está parada.
 *
 * Nunca lanza: un logo que no cargó no puede impedir que se imprima una receta.
 */
export async function getMarcaInstitucion(medicoCodigo) {
  if (!medicoCodigo) return { institucion_id: null, nombre: null, logo: null };
  const { data, error } = await supabase.rpc('get_marca_institucion', { p_medico_codigo: medicoCodigo });
  if (error) {
    console.error('getMarcaInstitucion error:', error);
    return { institucion_id: null, nombre: null, logo: null };
  }
  return data || { institucion_id: null, nombre: null, logo: null };
}

/** Cambia (o quita, mandando null) el logo de una clínica. Solo admin. */
export async function setLogoInstitucion(institucionId, logoDataUri) {
  const { data, error } = await supabase.rpc('set_logo_institucion', {
    p_institucion_id: institucionId,
    p_logo: logoDataUri || null,
  });
  if (error) throw error;
  return data;
}

/**
 * Todas las respuestas de una clínica, ya tabuladas para bajarlas a una hoja.
 *
 * La base arma las columnas (en el orden del formulario, con la etiqueta del
 * snapshot más reciente) y una fila por hoja. El cliente solo las pega: si
 * mañana cambia el formulario, no hay nada que tocar acá.
 *
 * Devuelve { columnas: [{clave, seccion, etiqueta}], filas: [...] }.
 */
export async function exportarAdmision(institucionId, { estado = null, limite = 2000 } = {}) {
  const { data, error } = await supabase.rpc('exportar_admision', {
    p_institucion_id: institucionId,
    p_estado: estado,
    p_limite: limite,
  });
  if (error) throw error;
  return data || { columnas: [], filas: [] };
}

/** El link que se manda por WhatsApp. Query param y no ruta: no depende de
 *  ninguna reescritura del hosting, así que no hay forma de que dé 404. */
export function linkAdmision(token) {
  return `${RAIZ_APP}/?a=${encodeURIComponent(token)}`;
}

/** Abre WhatsApp con el mensaje y el link ya escritos. */
export function whatsappAdmision(token, { telefono = '', clinica = '' } = {}) {
  const texto = `Hola, le comparto la hoja de datos de ${clinica || 'la clínica'}. `
    + `Se llena desde el celular y toma unos minutos:\n\n${linkAdmision(token)}`;
  const num = String(telefono || '').replace(/\D/g, '');
  return `https://wa.me/${num}?text=${encodeURIComponent(texto)}`;
}

// ─── Módulos por clínica ─────────────────────────────────────────────────────
// Cada clínica opera distinto y eso es el producto, no un problema a resolver.
// La capacidad vive en el código para todos; la decisión de usarla vive por
// institución. Sin fila = apagado, y un doctor sin institución no tiene módulos.
//
// La pregunta se hace SOBRE UN DOCTOR, no sobre el usuario logueado: una
// secretaria puede atender doctores de dos clínicas distintas, y preguntar
// "¿qué módulos tiene ella?" daría la respuesta equivocada en una de las dos
// pantallas.
//
// Devuelve { 'pacientes.codigo': { activo, config }, ... } — una llamada por
// pantalla, no una por módulo.

export async function getModulosDoctor(medicoCodigo) {
  if (!medicoCodigo) return {};
  const { data, error } = await supabase.rpc('get_modulos_doctor', { p_medico_codigo: medicoCodigo });
  if (error) { console.error('getModulosDoctor error:', error); return {}; }
  return data || {};
}

/** Los módulos activos para el USUARIO, no para un doctor: es lo que decide qué
 *  entradas existen en el menú, donde todavía no hay un doctor en foco. Unión de
 *  todas sus clínicas — una secretaria de dos, donde solo una usa admisión, tiene
 *  que ver la entrada y elegir la clínica adentro. */
export async function getMisModulos() {
  const { data, error } = await supabase.rpc('get_mis_modulos');
  if (error) { console.error('getMisModulos error:', error); return {}; }
  return data || {};
}

/** Lectura segura del mapa: si algo falló al cargarlo, el módulo está apagado. */
export function moduloActivo(modulos, clave) {
  return Boolean(modulos && modulos[clave] && modulos[clave].activo);
}

/** Catálogo + estado de una clínica, para la pantalla de administración. */
export async function getModulosInstitucion(institucionId) {
  const { data, error } = await supabase.rpc('get_modulos_institucion', { p_institucion_id: institucionId });
  if (error) throw error;
  return data || [];
}

/** Enciende o apaga un módulo de una clínica. Solo admin. */
export async function setModuloInstitucion(institucionId, modulo, activo, config = null) {
  const { data, error } = await supabase.rpc('set_modulo_institucion', {
    p_institucion_id: institucionId,
    p_modulo: modulo,
    p_activo: activo,
    p_config: config,
  });
  if (error) throw error;
  return data || [];
}

// ─── Cupo simultáneo ─────────────────────────────────────────────────────────
// Cuántos pacientes puede tener el doctor a la misma hora. 1 es la agenda de
// toda la vida. Una clínica de fertilidad que atiende parejas, o un consultorio
// que en días cargados "acuacha" dos consultas en el mismo espacio, sube este
// número y la base deja de tratar el traslape como error.
//
// No es un permiso de sobrecupo abierto: el tope sigue existiendo, solo que
// ahora es configurable por doctor. Bajarlo no expulsa citas ya agendadas.

export async function getCupoAgenda(medicoCodigo) {
  if (!medicoCodigo) return 1;
  const { data, error } = await supabase.rpc('get_agenda_cupo', { p_medico_codigo: medicoCodigo });
  if (error) throw error;
  return Number(data) || 1;
}

export async function setCupoAgenda(medicoCodigo, cupo) {
  const { data, error } = await supabase.rpc('set_agenda_cupo', {
    p_medico_codigo: medicoCodigo,
    p_cupo: cupo,
  });
  if (error) throw error;
  return Number(data) || 1;
}

// ─── Horarios semanales del doctor ───────────────────────────────────────────
// dia_semana: 0=domingo … 6=sábado. hora_inicio/hora_fin en hora GT ('HH:MM').
// Ojo: si un doctor no tiene NINGUNA franja activa, la base no le aplica
// restricción de horario (regla de gracia, para no dejar sin agenda a quien
// todavía no la configuró). Los bloqueos sí aplican siempre.

export async function getHorariosMedico(medicoCodigo) {
  if (!medicoCodigo) return [];
  const { data, error } = await supabase
    .from('medico_horarios')
    .select('*')
    .eq('medico_codigo', medicoCodigo)
    .order('dia_semana', { ascending: true })
    .order('hora_inicio', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function crearHorarioMedico(h) {
  const { data, error } = await supabase
    .from('medico_horarios')
    .insert({
      medico_codigo: h.medico_codigo,
      dia_semana: Number(h.dia_semana),
      hora_inicio: h.hora_inicio,
      hora_fin: h.hora_fin,
      vigente_desde: h.vigente_desde || null,
      vigente_hasta: h.vigente_hasta || null,
      activo: h.activo !== false,
      notas: h.notas?.trim() || null,
    })
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

export async function actualizarHorarioMedico(id, patch) {
  const { data, error } = await supabase
    .from('medico_horarios').update(patch).eq('id', id).select('*').single();
  if (error) throw error;
  return data;
}

export async function eliminarHorarioMedico(id) {
  const { error } = await supabase.from('medico_horarios').delete().eq('id', id);
  if (error) throw error;
}

// ─── Bloqueos (vacaciones, congresos, quirófano) ─────────────────────────────
// La base rechaza crear un bloqueo encima de citas vivas, con el conteo exacto.

export async function getBloqueosMedico({ medicoCodigo, desde = null, hasta = null }) {
  if (!medicoCodigo) return [];
  let q = supabase
    .from('medico_bloqueos')
    .select('*')
    .eq('medico_codigo', medicoCodigo)
    .order('inicio', { ascending: true });
  if (desde) q = q.gte('fin', desde);      // solapa el rango pedido…
  if (hasta) q = q.lte('inicio', hasta);   // …por eso se cruzan los extremos
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export async function crearBloqueoMedico({ medico_codigo, inicio, fin, motivo }) {
  const { data, error } = await supabase
    .from('medico_bloqueos')
    .insert({ medico_codigo, inicio, fin, motivo: motivo?.trim() || null })
    .select('*')
    .single();
  if (error) throw error;
  return data;
}

export async function eliminarBloqueoMedico(id) {
  const { error } = await supabase.from('medico_bloqueos').delete().eq('id', id);
  if (error) throw error;
}

/**
 * Bitácora de la agenda. El médico ve todo lo de su código; la secretaria, solo
 * lo que hizo ella. Es append-only: no hay helper de update ni de delete a
 * propósito — la base los rechaza aunque se intenten.
 */
export async function getBitacoraAgenda({ medicoCodigo = null, limite = 100 } = {}) {
  let q = supabase
    .from('auditoria')
    .select('id, at, tabla, registro_id, operacion, actor_nombre, actor_role, medico_codigo, cambios')
    .order('at', { ascending: false })
    .limit(limite);
  if (medicoCodigo) q = q.eq('medico_codigo', medicoCodigo);
  const { data, error } = await q;
  if (error) { console.error('getBitacoraAgenda error:', error); return []; }
  return data || [];
}

// ───────────────────────────────────────────────────────────
// MÉDICO — Home de Mi Clínica. RPC get_med_dashboard().
// Devuelve { pacientes, examenes, monto, listos, pendientes } del mes,
// ya filtrado server-side por el médico autenticado.
export async function getMedDashboard() {
  const { data, error } = await supabase.rpc('get_med_dashboard');
  if (error) { console.error('getMedDashboard error:', error); throw error; }
  return data || {};
}
// ───────────────────────────────────────────────────────────
// MÉDICO — Campana de notificaciones (med_notificaciones).
export async function getMedNotificaciones() {
  const { data, error } = await supabase
    .from('med_notificaciones')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(30);
  if (error) { console.error('getMedNotificaciones error:', error); return []; }
  return data || [];
}

export async function marcarNotifLeida(id) {
  const { error } = await supabase
    .from('med_notificaciones')
    .update({ leida: true })
    .eq('id', id);
  if (error) console.error('marcarNotifLeida error:', error);
}

export function subscribeMedNotificaciones(onInsert) {
  const ch = supabase
    .channel('med-notif-' + Math.random().toString(36).slice(2))
    .on('postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'med_notificaciones' },
      (payload) => { try { onInsert(payload.new); } catch (e) {} })
    .subscribe();
  return ch;
}


// ═══════════════════════════════════════════════════════════════════════════════
// BLOQUE 2b — Helpers del workspace de consulta
// Anexar al final de src/supabase.js (mismo módulo donde vive el cliente `supabase`).
// Patrón idéntico a los helpers existentes: retornan data directo, lanzan en error.
// Las columnas medico_codigo / med_paciente_id las estampan los triggers BEFORE WRITE,
// por eso el cliente NUNCA las envía.
// ═══════════════════════════════════════════════════════════════════════════════

// ── med_pacientes ──────────────────────────────────────────────────────────────
export async function getMedPaciente(id) {
  const { data, error } = await supabase
    .from('med_pacientes').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  return data;
}



// Resuelve med_paciente desde una orden de lab vía el puente lab_paciente_id.
// records.paciente_id → pacientes.id ← med_pacientes.lab_paciente_id
export async function getMedPacientePorLabPacienteId(labPacienteId) {
  if (!labPacienteId) return null;
  const { data, error } = await supabase
    .from('med_pacientes').select('*').eq('lab_paciente_id', labPacienteId).maybeSingle();
  if (error) throw error;
  return data;
}
// Fallback por DPI cuando el puente lab_paciente_id viene null (Camino 2).
// pacientes.id → pacientes.dpi → med_pacientes.dpi (acotado por RLS al médico).
// Si encuentra la fila y su lab_paciente_id está vacío, rellena el puente al
// vuelo (best-effort, no bloqueante) para que la próxima vez resuelva directo.
export async function getMedPacientePorDpiDeLab(labPacienteId) {
  if (!labPacienteId) return null;

  // 1) DPI canónico del paciente de lab.
  const { data: pac, error: pErr } = await supabase
    .from('pacientes').select('dpi').eq('id', labPacienteId).maybeSingle();
  if (pErr) throw pErr;
  const dpi = pac?.dpi;
  if (!dpi) return null; // sin DPI (o dpi_revisar) → no resuelve, cae al alert.

  // 2) Roster del médico por DPI. limit(1) evita el throw de maybeSingle si
  //    hubiera duplicados; RLS ya acota a las filas del propio doctor.
  const { data: rows, error: mErr } = await supabase
    .from('med_pacientes').select('*').eq('dpi', dpi).limit(1);
  if (mErr) throw mErr;
  const mp = rows?.[0] || null;
  if (!mp) return null;

  // 3) Auto-sanación del puente (best-effort). Si falla, noop: se reintenta
  //    en la próxima apertura. Nunca rompe la apertura de la consulta.
  if (!mp.lab_paciente_id) {
    const { error: upErr } = await supabase
      .from('med_pacientes').update({ lab_paciente_id: labPacienteId }).eq('id', mp.id);
    if (!upErr) mp.lab_paciente_id = labPacienteId; // refleja en el objeto devuelto
  }

  return mp;
}

export async function actualizarDatosCriticos(id, { alergias, datos_criticos }) {
  const { data, error } = await supabase
    .from('med_pacientes')
    .update({ alergias, datos_criticos })
    .eq('id', id)
    .select()
    .maybeSingle();
  if (error) throw error;
  return data;
}

// ── consultas ────────────────────────────────────────────────────────────────
export async function getConsultaAbierta(medPacienteId) {
  const { data, error } = await supabase
    .from('consultas')
    .select('*')
    .eq('med_paciente_id', medPacienteId)
    .eq('estado', 'abierta')
    .order('fecha', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function crearConsulta({ med_paciente_id, cita_id = null, motivo = null }) {
  const { data, error } = await supabase
    .from('consultas')
    .insert({ med_paciente_id, cita_id, motivo }) // medico_codigo lo estampa el trigger
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function cerrarConsulta(id) {
  const { data, error } = await supabase
    .from('consultas')
    .update({ estado: 'cerrada', fecha_cierre: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── addenda ───────────────────────────────────────────────────────────────────
export async function getConsultasDelPaciente(medPacienteId) {
  const { data, error } = await supabase
    .from('consultas')
    .select('*')
    .eq('med_paciente_id', medPacienteId)
    .order('fecha', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function getAddendaDelPaciente(medPacienteId) {
  const { data, error } = await supabase
    .from('addenda')
    .select('*')
    .eq('med_paciente_id', medPacienteId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function crearAddendum({ consulta_id, texto }) {
  const { data, error } = await supabase
    .from('addenda')
    .insert({ consulta_id, texto }) // med_paciente_id + medico_codigo los estampa el trigger
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── notas_evolucion ──────────────────────────────────────────────────────────
export async function getNotasEvolucion(medPacienteId) {
  // Trae las notas propias del expediente Y las de consulta conjunta escritas en
  // un expediente vinculado. Cada fila viene con tres banderas:
  //   es_de_este_expediente · compartida_con[] · origen_nombre
  if (!medPacienteId) return [];
  const { data, error } = await supabase
    .rpc('get_notas_expediente', { p_med_paciente_id: medPacienteId });
  if (error) throw error;
  return data || [];
}

/** Marca (o desmarca) una nota como de consulta conjunta. No copia: comparte. */
export async function compartirNota(notaId, compartir = true, medPacientes = null) {
  const { data, error } = await supabase.rpc('compartir_nota', {
    p_nota_id: notaId,
    p_compartir: compartir,
    p_med_pacientes: medPacientes,
  });
  if (error) throw error;
  return data;
}

/** Cuántas notas conjuntas hay en juego antes de quitar un vínculo. */
export async function contarNotasConjuntas(grupoId, medPacienteId) {
  const { data, error } = await supabase.rpc('contar_notas_conjuntas', {
    p_grupo_id: grupoId,
    p_med_paciente_id: medPacienteId,
  });
  if (error) { console.error('contarNotasConjuntas error:', error); return 0; }
  return data || 0;
}

export async function crearNota({ consulta_id, contenido }) {
  // medico_codigo + med_paciente_id los estampa el trigger desde la consulta padre.
  const { data, error } = await supabase
    .from('notas_evolucion')
    .insert({ consulta_id, contenido })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function actualizarNota(id, { contenido }) {
  const { data, error } = await supabase
    .from('notas_evolucion')
    .update({ contenido })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── signos_vitales ───────────────────────────────────────────────────────────
export async function getSignosVitalesPorConsulta(consultaId) {
  const { data, error } = await supabase
    .from('signos_vitales')
    .select('*')
    .eq('consulta_id', consultaId)
    .order('fecha', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function getSignosVitalesPorPaciente(medPacienteId) {
  const { data, error } = await supabase
    .from('signos_vitales')
    .select('*')
    .eq('med_paciente_id', medPacienteId)
    .order('fecha', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function guardarSignosVitales({ consulta_id, ...campos }) {
  // medico_codigo + med_paciente_id los estampa el trigger desde la consulta.
  // imc es columna generada: NUNCA se envía. Solo se mandan campos con valor;
  // los vacíos quedan NULL (todos opcionales).
  const limpio = {};
  for (const [k, v] of Object.entries(campos)) {
    if (k === 'imc') continue;
    if (v !== '' && v !== null && v !== undefined) limpio[k] = v;
  }
  const { data, error } = await supabase
    .from('signos_vitales')
    .upsert({ consulta_id, ...limpio }, { onConflict: 'consulta_id' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── recetas (Bloque 2c) ───────────────────────────────────────────────────────
// Modelo: UNA receta ACTIVA por consulta (uq_recetas_activa parcial). El guardado
// hace upsert de la cabecera activa + REEMPLAZO de items (borrar+insertar).
// med_paciente_id / medico_codigo los estampan los triggers desde la consulta padre,
// por eso el cliente sólo manda consulta_id y el contenido.

/** Lee la receta ACTIVA de una consulta (con sus items ordenados) o null. */
export async function getRecetaActiva(consultaId) {
  if (!consultaId) return null;
  const { data: receta, error } = await supabase
    .from('recetas')
    .select('*')
    .eq('consulta_id', consultaId)
    .eq('estado', 'activa')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  if (!receta) return null;

  const { data: items, error: e2 } = await supabase
    .from('receta_items')
    .select('*')
    .eq('receta_id', receta.id)
    .order('orden', { ascending: true });
  if (e2) throw e2;

  return { ...receta, items: items || [] };
}

// ── Farmacia · catálogo de productos (farm_productos) ─────────────────────────
// Catálogo de la clínica (RLS: el médico ve el de sus instituciones). Se usa para
// calcular la cantidad a despachar en la receta. Cache corto en memoria: cambia
// poco y lo piden la receta y el ciclo.
let _catalogoFarmacia = { t: 0, rows: null };
export async function getCatalogoFarmacia({ forzar = false } = {}) {
  if (!forzar && _catalogoFarmacia.rows && Date.now() - _catalogoFarmacia.t < 5 * 60 * 1000) {
    return _catalogoFarmacia.rows;
  }
  const { data, error } = await supabase
    .from('farm_productos')
    .select('id, institucion_id, familia, nombre, alias, forma, via, contenido, unidad, multidosis, unidades_por_empaque, se_vende_por, nombre_unidad, nombre_empaque, activo, orden, insumos')
    .eq('activo', true)
    .order('orden', { ascending: true })
    .limit(2000);
  if (error) throw error;
  const rows = (data || []).map(r => ({ ...r, contenido: r.contenido == null ? null : Number(r.contenido) }));
  _catalogoFarmacia = { t: Date.now(), rows };
  return rows;
}

// ── Farmacia · catálogo editable (recepción / manager / admin) ────────────────
// La lectura va directo a la tabla (RLS). La escritura SIEMPRE por el RPC
// farm_producto_guardar: valida clínica, módulo y permiso, y la bitácora la
// escribe un trigger. No hay borrado: un producto se desactiva.

export function invalidarCatalogoFarmacia() {
  _catalogoFarmacia = { t: 0, rows: null };
}

export async function getProductosFarmacia({ institucionId } = {}) {
  let q = supabase
    .from('farm_productos')
    .select('*')
    .order('familia', { ascending: true })
    .order('orden', { ascending: true })
    .limit(2000);
  if (institucionId) q = q.eq('institucion_id', institucionId);
  const { data, error } = await q;
  if (error) throw error;
  return (data || []).map(r => ({
    ...r,
    contenido: r.contenido == null ? null : Number(r.contenido),
    precio: r.precio == null ? null : Number(r.precio),
  }));
}

export async function guardarProductoFarmacia(producto) {
  const { data, error } = await supabase.rpc('farm_producto_guardar', { p: producto });
  if (error) {
    // El RPC trae el mensaje para la persona en `hint`.
    const e = new Error(error.hint || error.message);
    e.codigo = error.message;
    throw e;
  }
  invalidarCatalogoFarmacia();
  return data;
}

export async function getBitacoraProductoFarmacia(productoId, limite = 30) {
  const { data, error } = await supabase
    .from('farm_productos_bitacora')
    .select('id, accion, cambios, usuario_nombre, created_at')
    .eq('producto_id', productoId)
    .order('created_at', { ascending: false })
    .limit(limite);
  if (error) throw error;
  return data || [];
}

// ── Farmacia · inventario (paso 2) ────────────────────────────────────────────
// La existencia es la suma de movimientos (vista farm_existencias); nunca se
// escribe. Entradas y conteos van por RPC, que valida clínica, módulo y permiso.

export async function getExistenciasFarmacia({ institucionId } = {}) {
  let q = supabase.from('farm_existencias').select('*').limit(5000);
  if (institucionId) q = q.eq('institucion_id', institucionId);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

export async function getMovimientosFarmacia(productoId, limite = 100) {
  const { data, error } = await supabase
    .from('farm_movimientos')
    .select('id, tipo, unidades, motivo, usuario_nombre, created_at, farm_lotes ( lote, vence )')
    .eq('producto_id', productoId)
    .order('created_at', { ascending: false })
    .limit(limite);
  if (error) throw error;
  return data || [];
}

function errorFarmacia(error) {
  const e = new Error(error.hint || error.message);
  e.codigo = error.message;
  return e;
}

export async function registrarEntradaFarmacia({ productoId, lote = null, vence = null, unidades, nota = null }) {
  const { data, error } = await supabase.rpc('farm_registrar_entrada', {
    p_producto_id: productoId, p_lote: lote || null, p_vence: vence || null,
    p_unidades: Number(unidades), p_nota: nota || null,
  });
  if (error) throw errorFarmacia(error);
  return data;
}

/** items = [{producto_id, lote, vence, contado}] → cuántos movimientos generó. */
export async function registrarConteoFarmacia({ institucionId = null, items, motivo = null }) {
  const { data, error } = await supabase.rpc('farm_registrar_conteo', {
    p_institucion_id: institucionId, p_items: items, p_motivo: motivo || null,
  });
  if (error) throw errorFarmacia(error);
  return data;
}

// ── recetas_plantillas (Fase 1) ───────────────────────────────────────────────
// Plantillas de receta = preferencia del médico (mutable, owner-only por RLS).
// medico_codigo lo estampa el trigger server-side; el cliente sólo manda nombre+items.
// items = MISMO shape que filaVacia(): {farmaco,dosis,frecuencia,duracion,indicaciones}.

/** Lista las plantillas del médico autenticado (orden alfabético). */
export async function listarPlantillasRecetas() {
  const { data, error } = await supabase
    .from('recetas_plantillas')
    .select('*')
    .order('nombre', { ascending: true });
  if (error) throw error;
  return data || [];
}

/**
 * Crea/actualiza (por nombre) una plantilla. medico_codigo lo estampa el trigger.
 * upsert onConflict (medico_codigo,nombre): re-guardar con un nombre existente lo
 * SOBRESCRIBE en vez de duplicar. El trigger BEFORE INSERT estampa medico_codigo
 * ANTES de que Postgres evalúe el conflicto, así que la inferencia del índice único
 * funciona aunque el cliente no mande medico_codigo. Sólo persiste las 5 keys.
 */
export async function guardarPlantillaReceta({ nombre, items = [] }) {
  const limpios = (items || [])
    .map(it => ({
      farmaco:      (it.farmaco || '').trim(),
      dosis:        (it.dosis || '').trim(),
      frecuencia:   (it.frecuencia || '').trim(),
      duracion:     (it.duracion || '').trim(),
      indicaciones: (it.indicaciones || '').trim(),
    }))
    .filter(it => it.farmaco);

  const { data, error } = await supabase
    .from('recetas_plantillas')
    .upsert(
      { nombre: (nombre || '').trim(), items: limpios },
      { onConflict: 'medico_codigo,nombre' }
    )
    .select()
    .single();
  if (error) throw error;
  return data;
}

/** Borra una plantilla por id (RLS garantiza que sea del propio médico). */
export async function borrarPlantillaReceta(id) {
  const { error } = await supabase
    .from('recetas_plantillas')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

/**
 * Crea/actualiza la receta activa de la consulta y REEMPLAZA sus items.
 * NOTA: el reemplazo (borrar+insertar) NO es atómico desde el cliente — supabase-js
 * no envuelve varios statements en transacción. RLS protege la propiedad y un fallo
 * deja la receta recuperable. Si más adelante se quiere atomicidad real, conviene un
 * RPC `guardar_receta(...)` SECURITY DEFINER (no requerido para 2c).
 */
export async function guardarReceta({
  consulta_id,
  receta_id = null,
  diagnostico = null,
  diagnosticos = [],
  indicaciones_generales = null,
  observaciones = null,
  items = [],
  anexo_ciclo,            // undefined = no tocar; null = quitar el anexo
}) {
  // Foto del calendario del ciclo de fertilidad (hoja anexa del PDF).
  const extra = anexo_ciclo === undefined ? {} : { anexo_ciclo };
  // 1) Cabecera activa
  let receta;
  if (receta_id) {
    const { data, error } = await supabase
      .from('recetas')
      .update({ diagnostico, diagnosticos, indicaciones_generales, observaciones, ...extra })
      .eq('id', receta_id)
      .select()
      .single();
    if (error) throw error;
    receta = data;
  } else {
    // medico_codigo + med_paciente_id los estampa el trigger desde la consulta.
    const { data, error } = await supabase
      .from('recetas')
      .insert({ consulta_id, diagnostico, diagnosticos, indicaciones_generales, observaciones, ...extra })
      .select()
      .single();
    if (error) throw error;
    receta = data;
  }

  // 2) Reemplazo de items: borrar los existentes…
  const { error: delErr } = await supabase
    .from('receta_items')
    .delete()
    .eq('receta_id', receta.id);
  if (delErr) throw delErr;

  // …e insertar los nuevos (farmaco es NOT NULL → se filtran filas vacías).
  const limpios = (items || [])
    .map((it, i) => ({
      receta_id: receta.id,
      farmaco: (it.farmaco || '').trim(),
      dosis: (it.dosis || '').trim() || null,
      frecuencia: (it.frecuencia || '').trim() || null,
      duracion: (it.duracion || '').trim() || null,
      indicaciones: (it.indicaciones || '').trim() || null,
      del_ciclo: it.del_ciclo === true,
      // Cantidad a despachar (utils/cantidadReceta.js). Manual = la escribió el médico.
      cantidad: (it.cantidad || '').trim() || null,
      cantidad_detalle: it.cantidad_manual ? null : (it.cantidad_detalle || null),
      cantidad_manual: it.cantidad_manual === true && !!(it.cantidad || '').trim(),
      orden: i,
    }))
    .filter(it => it.farmaco);

  let nuevos = [];
  if (limpios.length) {
    const { data, error: insErr } = await supabase
      .from('receta_items')
      .insert(limpios)
      .select()
      .order('orden', { ascending: true });
    if (insErr) throw insErr;
    nuevos = data || [];
  }

  return { ...receta, items: nuevos };
}

/**
 * Marca la receta como 'reemplazada': era válida y ya se entregó, pero otro día
 * se generó una más nueva (ciclo de fertilidad). Libera el índice único activo
 * y la anterior queda como historial, sin confundirse con una anulada.
 */
export async function reemplazarReceta(id) {
  const { data, error } = await supabase
    .from('recetas')
    .update({ estado: 'reemplazada' })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}

/** Marca la receta como anulada (estado='anulada'); libera el índice único activo. */
export async function anularReceta(id) {
  const { data, error } = await supabase
    .from('recetas')
    .update({ estado: 'anulada' })
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data;
}
// ── solicitudes de laboratorio (Bloque 3) ─────────────────────────────────────
// Mismo patrón que recetas: UNA solicitud ACTIVA por consulta (uq_solicitudes_activa).
// El cliente manda solo consulta_id + contenido; medico_codigo / med_paciente_id los
// estampa el trigger desde la consulta padre. El guardado hace upsert de la cabecera
// activa + REEMPLAZO de items (borrar+insertar) — NO atómico desde el cliente, igual
// que guardarReceta; RLS protege la propiedad. Atomicidad real más adelante:
// RPC guardar_solicitud(...) SECURITY DEFINER (no requerido aquí).

/** Lee la solicitud ACTIVA de una consulta (con sus items ordenados) o null. */
export async function getSolicitudActiva(consultaId) {
  if (!consultaId) return null;
  const { data: sol, error } = await supabase
    .from('solicitudes_labs')
    .select('*')
    .eq('consulta_id', consultaId)
    .eq('estado', 'activa')
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  if (!sol) return null;

  const { data: items, error: e2 } = await supabase
    .from('solicitud_items')
    .select('*')
    .eq('solicitud_id', sol.id)
    .order('orden', { ascending: true });
  if (e2) throw e2;

  return { ...sol, items: items || [] };
}

/**
 * Crea/actualiza la solicitud activa de la consulta y REEMPLAZA sus items.
 * items: [{ exam_id, exam_name, categoria }]
 */
export async function guardarSolicitud({
  consulta_id,
  solicitud_id = null,
  prioridad = 'rutina',
  ayuno = false,
  diagnostico_presuntivo = null,
  observaciones = null,
  items = [],
}) {
  // 1) Cabecera activa
  let sol;
  if (solicitud_id) {
    const { data, error } = await supabase
      .from('solicitudes_labs')
      .update({ prioridad, ayuno, diagnostico_presuntivo, observaciones })
      .eq('id', solicitud_id)
      .select()
      .single();
    if (error) throw error;
    sol = data;
  } else {
    // medico_codigo + med_paciente_id los estampa el trigger desde la consulta.
    const { data, error } = await supabase
      .from('solicitudes_labs')
      .insert({ consulta_id, prioridad, ayuno, diagnostico_presuntivo, observaciones })
      .select()
      .single();
    if (error) throw error;
    sol = data;
  }

  // 2) Reemplazo de items (borrar + insertar). No atómico; RLS protege.
  const { error: eDel } = await supabase
    .from('solicitud_items')
    .delete()
    .eq('solicitud_id', sol.id);
  if (eDel) throw eDel;

  // Defensa en profundidad: nunca guardar el mismo examen dos veces (por
  // exam_id, o por nombre normalizado). La BD además lo impide con
  // uq_solicitud_items_exam.
  const _normN = (x) => (x || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  const _vistos = new Set();
  const limpios = (items || [])
    .filter(it => (it.exam_name || '').trim())
    .filter(it => {
      const kId = it.exam_id != null && it.exam_id !== '' ? 'id:' + String(it.exam_id) : null;
      const kNom = 'n:' + _normN(it.exam_name);
      if ((kId && _vistos.has(kId)) || _vistos.has(kNom)) return false;
      if (kId) _vistos.add(kId);
      _vistos.add(kNom);
      return true;
    })
    .map((it, i) => ({
      solicitud_id: sol.id,
      orden: i,
      exam_id: it.exam_id ?? null,
      exam_name: (it.exam_name || '').trim(),
      categoria: it.categoria || null,
    }));

  if (limpios.length) {
    const { error: eIns } = await supabase.from('solicitud_items').insert(limpios);
    if (eIns) throw eIns;
  }

  // 3) Re-lee items ordenados para devolver la forma final.
  const { data: itemsFin, error: e3 } = await supabase
    .from('solicitud_items')
    .select('*')
    .eq('solicitud_id', sol.id)
    .order('orden', { ascending: true });
  if (e3) throw e3;

  return { ...sol, items: itemsFin || [] };
}

/** Marca la solicitud como anulada (deja libre el slot activo de la consulta). */
export async function anularSolicitud(solicitudId) {
  const { data, error } = await supabase
    .from('solicitudes_labs')
    .update({ estado: 'anulada' })
    .eq('id', solicitudId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════════
// FASE 2 — Favoritos + paquetes de laboratorio (owner-only por RLS)
// ═══════════════════════════════════════════════════════════════════════════════

/** Lista los exámenes favoritos del médico autenticado (RLS owner-only). */
export async function listFavoritos() {
  const { data, error } = await supabase
    .from('medico_favoritos')
    .select('*')
    .order('exam_name', { ascending: true });
  if (error) throw error;
  return data || [];
}

/**
 * Prende/apaga la estrella de un examen de catálogo.
 * on=true  → inserta (idempotente: ignora duplicado por unique medico_codigo+exam_id)
 * on=false → borra por exam_id (RLS lo acota al dueño)
 * medico_codigo lo estampa el trigger.
 */
export async function toggleFavorito({ exam_id, exam_name, categoria = null, on }) {
  if (!exam_id) throw new Error('exam_id requerido para favorito');
  if (on) {
    const { error } = await supabase
      .from('medico_favoritos')
      .insert({ exam_id, exam_name, categoria });
    // 23505 = duplicado (ya era favorito) → lo tratamos como éxito.
    if (error && error.code !== '23505') throw error;
    return true;
  } else {
    const { error } = await supabase
      .from('medico_favoritos')
      .delete()
      .eq('exam_id', exam_id);
    if (error) throw error;
    return false;
  }
}

/** Lista los paquetes del médico con sus items (ordenados por `orden`). */
export async function listPaquetes() {
  const { data, error } = await supabase
    .from('medico_paquetes')
    .select('*, items:medico_paquete_items(*)')
    .order('nombre', { ascending: true });
  if (error) throw error;
  return (data || []).map(p => ({
    ...p,
    items: (p.items || [])
      .slice()
      .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0)),
  }));
}

/**
 * Crea/renombra un paquete y REEMPLAZA sus items. Mismo patrón que guardarSolicitud.
 * items: [{ exam_id, exam_name, categoria }]
 */
export async function guardarPaquete({ paquete_id = null, nombre, items = [] }) {
  const nom = (nombre || '').trim();
  if (!nom) throw new Error('El paquete necesita un nombre.');

  // 1) Cabecera (medico_codigo lo estampa el trigger en insert).
  let paq;
  if (paquete_id) {
    const { data, error } = await supabase
      .from('medico_paquetes')
      .update({ nombre: nom })
      .eq('id', paquete_id)
      .select()
      .single();
    if (error) throw error;
    paq = data;
  } else {
    const { data, error } = await supabase
      .from('medico_paquetes')
      .insert({ nombre: nom })
      .select()
      .single();
    if (error) throw error;
    paq = data;
  }

  // 2) Reemplazo de items (borrar + insertar). RLS protege.
  const { error: eDel } = await supabase
    .from('medico_paquete_items')
    .delete()
    .eq('paquete_id', paq.id);
  if (eDel) throw eDel;

  const limpios = (items || [])
    .filter(it => (it.exam_name || '').trim())
    .map((it, i) => ({
      paquete_id: paq.id,
      orden: i,
      exam_id: it.exam_id ?? null,
      exam_name: (it.exam_name || '').trim(),
      categoria: it.categoria || null,
    }));

  if (limpios.length) {
    const { error: eIns } = await supabase.from('medico_paquete_items').insert(limpios);
    if (eIns) throw eIns;
  }

  // 3) Re-lee items ordenados.
  const { data: itemsFin, error: e3 } = await supabase
    .from('medico_paquete_items')
    .select('*')
    .eq('paquete_id', paq.id)
    .order('orden', { ascending: true });
  if (e3) throw e3;

  return { ...paq, items: itemsFin || [] };
}

/** Borra un paquete completo (los items caen por CASCADE). */
export async function borrarPaquete(paqueteId) {
  const { error } = await supabase
    .from('medico_paquetes')
    .delete()
    .eq('id', paqueteId);
  if (error) throw error;
  return true;
}

// ============================================================
// ADJUNTOS CLÍNICOS (bucket privado 'adjuntos-clinicos')
// Path en Storage: {medico_codigo}/{med_paciente_id}/{id}.{ext}
// El primer folder = llave de ownership (espeja la RLS).
// ============================================================

const ADJ_BUCKET = 'adjuntos-clinicos';
const ADJ_MAX_BYTES = 20 * 1024 * 1024; // 20 MB
const ADJ_MIME_EXT = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'application/pdf': 'pdf',
};

// Categorías de la tabla (CHECK de `adjuntos.categoria`). 'otro' es del médico:
// para la secretaria el servidor exige una de las cuatro con nombre — la
// categoría es lo que decide qué archivos ve, así que no puede quedar en blanco.
export const ADJ_CATEGORIAS = [
  { valor: 'consentimiento', label: 'Consentimiento', permiso: 'archivos.consentimientos' },
  { valor: 'identificacion', label: 'Identificación', permiso: 'archivos.identificaciones' },
  { valor: 'estudio_previo', label: 'Estudio previo', permiso: 'archivos.estudios_previos' },
  { valor: 'facturacion',    label: 'Facturación',    permiso: 'archivos.facturacion' },
];
export const ADJ_CATEGORIA_LABEL = {
  ...Object.fromEntries(ADJ_CATEGORIAS.map(c => [c.valor, c.label])),
  otro: 'Sin categoría',
};

// Resuelve el código del médico actual (param o RPC fallback)
async function _resolverCodigoMedico(codigo) {
  if (codigo) return codigo;
  const { data, error } = await supabase.rpc('get_my_doctor_codigo');
  if (error) throw new Error('No se pudo resolver el código del médico: ' + error.message);
  // Una secretaria no tiene código propio: el suyo es el del doctor cuyo
  // expediente está mirando, y ese viene por parámetro. Si llegamos acá sin
  // código es un bug de la pantalla, no una sesión rota.
  if (!data) throw new Error('Falta el código del médico dueño del expediente.');
  return data;
}

/**
 * Sube un archivo y crea su fila en `adjuntos`.
 * @param {File} file - archivo del input
 * @param {Object} opts
 * @param {string} opts.med_paciente_id - obligatorio
 * @param {string|null} [opts.consulta_id] - opcional (anclar a consulta)
 * @param {string} [opts.medico_codigo] - opcional (evita un round-trip)
 * @param {string} [opts.categoria] - 'consentimiento' | 'identificacion' |
 *        'estudio_previo' | 'facturacion'. Obligatoria para la secretaria; si no
 *        se pasa, la base la deja en 'otro' (uso del médico).
 * @returns {Promise<Object>} la fila insertada en `adjuntos`
 */
export async function subirAdjunto(file, { med_paciente_id, consulta_id = null, medico_codigo, categoria = null } = {}) {
  if (!file) throw new Error('No se recibió ningún archivo.');
  if (!med_paciente_id) throw new Error('Falta med_paciente_id.');

  // --- Validación cliente (el bucket y la tabla la refuerzan server-side) ---
  const mime = file.type;
  const ext = ADJ_MIME_EXT[mime];
  if (!ext) {
    throw new Error('Tipo no permitido. Solo JPG, PNG, WEBP o PDF.');
  }
  if (file.size <= 0) throw new Error('El archivo está vacío.');
  if (file.size > ADJ_MAX_BYTES) {
    throw new Error('El archivo supera el límite de 20 MB.');
  }

  const codigo = await _resolverCodigoMedico(medico_codigo);
  const id = crypto.randomUUID();
  const path = `${codigo}/${med_paciente_id}/${id}.${ext}`;

  // --- 1) Subir a Storage ---
  const { error: upErr } = await supabase.storage
    .from(ADJ_BUCKET)
    .upload(path, file, { contentType: mime, upsert: false });
  if (upErr) throw new Error('Error al subir el archivo: ' + upErr.message);

  // --- 2) Insertar la fila (el trigger estampa medico_codigo/created_at) ---
  const { data, error: insErr } = await supabase
    .from('adjuntos')
    .insert({
      id,
      med_paciente_id,
      consulta_id,
      medico_codigo: codigo,
      storage_path: path,
      nombre_original: file.name,
      mime_type: mime,
      size_bytes: file.size,
      ...(categoria ? { categoria } : {}),
    })
    .select()
    .single();

  // --- Rollback: si el insert falla, borro el objeto huérfano ---
  if (insErr) {
    await supabase.storage.from(ADJ_BUCKET).remove([path]); // best-effort
    throw new Error('Error al registrar el adjunto: ' + insErr.message);
  }

  return data;
}

/**
 * Lista adjuntos. Si se pasa consulta_id, filtra por consulta;
 * si no, devuelve todos los del paciente (expediente completo).
 * @param {Object} opts
 * @param {string} opts.med_paciente_id - obligatorio
 * @param {string|null} [opts.consulta_id] - opcional
 * @returns {Promise<Array>}
 */
export async function listarAdjuntos({ med_paciente_id, consulta_id = null } = {}) {
  if (!med_paciente_id) throw new Error('Falta med_paciente_id.');

  let q = supabase
    .from('adjuntos')
    .select('id, med_paciente_id, consulta_id, storage_path, nombre_original, mime_type, size_bytes, categoria, created_at')
    .order('created_at', { ascending: false });

  if (consulta_id) {
    q = q.eq('consulta_id', consulta_id);
  } else {
    q = q.eq('med_paciente_id', med_paciente_id);
  }

  const { data, error } = await q;
  if (error) throw new Error('Error al listar adjuntos: ' + error.message);
  return data || [];
}

/**
 * Genera una URL firmada de vida corta para ver/descargar un adjunto.
 * NUNCA se usa URL pública (el bucket es privado).
 * @param {string} storage_path
 * @param {number} [expiresIn=60] - segundos
 * @returns {Promise<string>}
 */
export async function getAdjuntoUrl(storage_path, expiresIn = 60) {
  if (!storage_path) throw new Error('Falta storage_path.');
  const { data, error } = await supabase.storage
    .from(ADJ_BUCKET)
    .createSignedUrl(storage_path, expiresIn);
  if (error) throw new Error('Error al firmar URL: ' + error.message);
  return data.signedUrl;
}

/**
 * Borra un adjunto. La RLS decide si se permite (solo autor, y solo
 * si es suelto o su consulta está abierta). Borra fila → luego objeto.
 * @param {Object} adjunto - fila con { id, storage_path }
 * @returns {Promise<void>}
 */
export async function borrarAdjunto(adjunto) {
  if (!adjunto?.id) throw new Error('Adjunto inválido.');

  // 1) Borrar la fila. .select() devuelve las filas afectadas:
  //    si la RLS lo bloquea (consulta cerrada / no autor), vienen 0.
  const { data, error } = await supabase
    .from('adjuntos')
    .delete()
    .eq('id', adjunto.id)
    .select('id');
  if (error) throw new Error('Error al borrar el adjunto: ' + error.message);
  if (!data || data.length === 0) {
    throw new Error('No se puede borrar: la consulta está cerrada o no estás autorizado.');
  }

  // 2) Borrar el objeto en Storage (best-effort; la metadata ya no existe)
  if (adjunto.storage_path) {
    await supabase.storage.from(ADJ_BUCKET).remove([adjunto.storage_path]);
  }
}
// ═══════════════════════════════════════════════════════════════════════════════
// MIS ARCHIVOS — biblioteca personal del médico (Bloque 2 · Fase 3)
// Bucket privado 'medico-archivos'. Mismo patrón que adjuntos: Storage primero,
// rollback del objeto si el insert falla, y NUNCA URL pública (solo firmadas).
// La RLS de tabla y de Storage filtran por get_my_doctor_codigo().
// ═══════════════════════════════════════════════════════════════════════════════

const MED_ARCH_BUCKET = 'medico-archivos';

/**
 * Sube un archivo a la biblioteca personal del médico y crea su fila.
 * @param {File} file - archivo del input
 * @param {Object} [opts]
 * @param {string|null} [opts.descripcion] - descripción opcional
 * @param {string} [opts.medico_codigo] - opcional (evita un round-trip)
 * @returns {Promise<Object>} la fila insertada en `medico_archivos`
 */
export async function subirMiArchivo(file, { descripcion = null, medico_codigo } = {}) {
  if (!file) throw new Error('No se recibió ningún archivo.');

  // --- Validación cliente (el bucket y la tabla la refuerzan server-side) ---
  const mime = file.type;
  const ext = ADJ_MIME_EXT[mime];
  if (!ext) {
    throw new Error('Tipo no permitido. Solo JPG, PNG, WEBP o PDF.');
  }
  if (file.size <= 0) throw new Error('El archivo está vacío.');
  if (file.size > ADJ_MAX_BYTES) {
    throw new Error('El archivo supera el límite de 20 MB.');
  }

  const codigo = await _resolverCodigoMedico(medico_codigo);
  const id = crypto.randomUUID();
  const path = `${codigo}/${id}.${ext}`;

  // --- 1) Subir a Storage ---
  const { error: upErr } = await supabase.storage
    .from(MED_ARCH_BUCKET)
    .upload(path, file, { contentType: mime, upsert: false });
  if (upErr) throw new Error('Error al subir el archivo: ' + upErr.message);

  // --- 2) Insertar la fila (el trigger estampa medico_codigo) ---
  const { data, error: insErr } = await supabase
    .from('medico_archivos')
    .insert({
      id,
      medico_codigo: codigo,
      storage_path: path,
      nombre_original: file.name,
      descripcion,
      mime_type: mime,
      size_bytes: file.size,
    })
    .select()
    .single();

  // --- Rollback: si el insert falla, borro el objeto huérfano ---
  if (insErr) {
    await supabase.storage.from(MED_ARCH_BUCKET).remove([path]); // best-effort
    throw new Error('Error al registrar el archivo: ' + insErr.message);
  }

  return data;
}

/**
 * Lista la biblioteca personal del médico autenticado.
 * La RLS ya filtra por get_my_doctor_codigo(); no hace falta filtro cliente.
 * @returns {Promise<Array>}
 */
export async function listarMisArchivos() {
  const { data, error } = await supabase
    .from('medico_archivos')
    .select('id, storage_path, nombre_original, descripcion, mime_type, size_bytes, created_at')
    .order('created_at', { ascending: false });
  if (error) throw new Error('Error al listar archivos: ' + error.message);
  return data || [];
}

/**
 * Genera una URL firmada para ver/descargar/compartir un archivo propio.
 * NUNCA se usa URL pública (el bucket es privado).
 * @param {string} storage_path
 * @param {number} [expiresIn=60] - segundos (compartir usa 604800 = 7 días)
 * @returns {Promise<string>}
 */
export async function getMiArchivoUrl(storage_path, expiresIn = 60) {
  if (!storage_path) throw new Error('Falta storage_path.');
  const { data, error } = await supabase.storage
    .from(MED_ARCH_BUCKET)
    .createSignedUrl(storage_path, expiresIn);
  if (error) throw new Error('Error al firmar URL: ' + error.message);
  return data.signedUrl;
}

/**
 * Borra un archivo de la biblioteca. La RLS decide (solo el dueño).
 * Borra fila → luego objeto (los enlaces compartidos dejan de servir).
 * @param {Object} archivo - fila con { id, storage_path }
 * @returns {Promise<void>}
 */
export async function borrarMiArchivo(archivo) {
  if (!archivo?.id) throw new Error('Archivo inválido.');

  // 1) Borrar la fila. .select() devuelve las filas afectadas:
  //    si la RLS lo bloquea, vienen 0.
  const { data, error } = await supabase
    .from('medico_archivos')
    .delete()
    .eq('id', archivo.id)
    .select('id');
  if (error) throw new Error('Error al borrar el archivo: ' + error.message);
  if (!data || data.length === 0) {
    throw new Error('No se pudo borrar: no estás autorizado.');
  }

  // 2) Borrar el objeto en Storage (best-effort; la metadata ya no existe)
  if (archivo.storage_path) {
    await supabase.storage.from(MED_ARCH_BUCKET).remove([archivo.storage_path]);
  }
}

// ── Fase 4.1A: Preórdenes (bandeja del laboratorio) ──────────────────────────
/** Lista las preórdenes por estado ('pendiente' | 'orden_creada' | 'descartada'). */
export async function getPreordenes(estado = 'pendiente') {
  const { data, error } = await supabase.rpc('get_preordenes', { p_estado: estado });
  if (error) throw error;
  return data || [];
}

/**
 * Notas del laboratorio sobre Pases (las deja Betty: examen adicional pedido
 * por el paciente, a qué sede llega, que pidió no ser contactado…). El Pase no
 * se modifica: las notas viven en `pase_notas`. Devuelve { solicitud_id: [notas] }.
 * Solo lectura; RLS: admin, QB y operador.
 */
export async function getPaseNotas(solicitudIds = []) {
  if (!solicitudIds.length) return {};
  const { data, error } = await supabase
    .from('pase_notas')
    .select('id, solicitud_id, nota, origen, cotizacion, created_at')
    .in('solicitud_id', solicitudIds)
    .eq('simulado', false)
    .order('created_at', { ascending: true });
  if (error) throw error;
  const porPase = {};
  for (const n of data || []) (porPase[n.solicitud_id] = porPase[n.solicitud_id] || []).push(n);
  return porPase;
}

// ── Bandeja de WhatsApp (Betty) ────────────────────────────────────────────────
// Conversaciones de Betty que esperan a una persona. Lectura y toma por RPC
// (admin, QB, operador); el envío pasa por la función betty-webhook, que es la
// única con permiso de WhatsApp.
export async function bettyBandeja(todas = false) {
  const { data, error } = await supabase.rpc('betty_bandeja', { p_todas: todas });
  if (error) throw error;
  return data || [];
}
export async function bettyBandejaMensajes(conversacionId) {
  const { data, error } = await supabase.rpc('betty_bandeja_mensajes', { p_conv: conversacionId });
  if (error) throw error;
  return data || [];
}
export async function bettyBandejaTomar(conversacionId) {
  const { error } = await supabase.rpc('betty_bandeja_tomar', { p_conv: conversacionId });
  if (error) throw new Error(error.message);
}
export async function bettyBandejaSoltar(conversacionId, resuelta = false) {
  const { error } = await supabase.rpc('betty_bandeja_soltar', { p_conv: conversacionId, p_resuelta: resuelta });
  if (error) throw new Error(error.message);
}
/** Envía la respuesta de una persona. Devuelve { status, data } sin lanzar en errores HTTP. */
export async function bettyBandejaResponder(conversacionId, texto, extra = {}) {
  // extra: { sede } para la ubicación de una sede, o { archivo: { nombre, mime, base64 } }.
  return llamarBandeja({ conversacion_id: conversacionId, texto, ...extra });
}
/** Bloquear un número: Betty deja de contestarle y de escribirle, y sale de la bandeja. */
export async function bettyBloquear(waId, motivo = '') {
  const { error } = await supabase.rpc('betty_bloquear', { p_wa_id: waId, p_motivo: motivo || null });
  if (error) throw new Error(error.message);
}
export async function bettyDesbloquear(waId) {
  const { error } = await supabase.rpc('betty_desbloquear', { p_wa_id: waId });
  if (error) throw new Error(error.message);
}
/** Tareas de llamada pendientes: Pases que Betty no pudo contactar por WhatsApp (staff). */
export async function bettyLlamadas() {
  const { data, error } = await supabase.rpc('betty_llamadas_lista');
  if (error) throw new Error(error.message);
  return data || [];
}
/** Registra el resultado de una llamada; va a la nota del Pase y al CRM. */
export async function bettyLlamadaResolver(id, resultado, nota = null, fecha = null) {
  const { error } = await supabase.rpc('betty_llamada_resolver', {
    p_id: id, p_resultado: resultado, p_nota: nota || null, p_fecha: fecha || null,
  });
  if (error) throw new Error(error.message);
}
export async function bettyBloqueados() {
  const { data, error } = await supabase.rpc('betty_bloqueados_lista');
  if (error) throw new Error(error.message);
  return data || [];
}
/** Bitácora de la bandeja: quién tomó, soltó, marcó resuelta, respondió o abrió. */
export async function bettyBandejaAcciones(conversacionId) {
  const { data, error } = await supabase.rpc('betty_bandeja_acciones_de', { p_conv: conversacionId });
  if (error) throw new Error(error.message);
  return data || [];
}
/** Lo que Betty leyó de cada foto o PDF y lo que cotizó (solo admin). */
export async function bettyOrdenesLeidas(limite = 50) {
  const { data, error } = await supabase.rpc('betty_ordenes_leidas_lista', { p_limite: limite });
  if (error) throw new Error(error.message);
  return data || [];
}
/** Enlace temporal (5 min) al adjunto de un mensaje. Devuelve { status, data: { url } | { error } }. */
export async function bettyBandejaAbrir(mensajeId) {
  return llamarBandeja({ abrir: mensajeId });
}
async function llamarBandeja(cuerpo) {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) return { status: 401, data: { error: 'no_autenticado' } };
  let res;
  try {
    res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/betty-webhook`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, 'x-betty-bandeja': '1' },
      body: JSON.stringify(cuerpo),
    });
  } catch {
    return { status: 0, data: { error: 'sin_conexion' } };
  }
  let data = {};
  try { data = await res.json(); } catch { /* respuesta sin cuerpo */ }
  return { status: res.status, data };
}

// ── Alimentar a Betty (16-oct-2026): RPC betty_panel_*, solo admin ──
async function bettyPanel(fn, args = {}) {
  const { data, error } = await supabase.rpc(fn, args);
  if (error) throw new Error(error.message);
  return data;
}
/** Todo lo que el panel muestra: datos, horarios, cierres, plazas, sedes, internos e interruptores. */
export const bettyPanelEstado = () => bettyPanel('betty_panel_estado');
/** El bloque de texto exacto que recibe Betty y los cierres de las próximas 3 semanas. */
export const bettyPanelVistaPrevia = () => bettyPanel('betty_panel_vista_previa');
export const bettyPanelDatoGuardar = (d, nuevo, confirmaMonto) => bettyPanel('betty_panel_dato_guardar', {
  p_clave: d.clave, p_titulo: d.titulo, p_contenido: d.contenido, p_fuente: d.fuente || null, p_activo: d.activo,
  p_vigente_desde: d.vigente_desde || null, p_vigente_hasta: d.vigente_hasta || null, p_nuevo: nuevo, p_confirma_monto: !!confirmaMonto,
});
export const bettyPanelDatoActivo = (clave, activo) => bettyPanel('betty_panel_dato_activo', { p_clave: clave, p_activo: activo });
export const bettyPanelDatosOrdenar = (claves) => bettyPanel('betty_panel_datos_ordenar', { p_claves: claves });
export const bettyPanelHorarioGuardar = (sede, dias) => bettyPanel('betty_panel_horario_guardar', { p_sede: sede, p_dias: dias });
export const bettyPanelCierreGuardar = (c) => bettyPanel('betty_panel_cierre_guardar', {
  p_id: c.id || null, p_nombre: c.nombre, p_desde: c.desde, p_hasta: c.hasta, p_sede: c.sede || null,
  p_tipo: c.tipo, p_abre: c.abre || null, p_cierra: c.cierra || null,
});
export const bettyPanelCierreBorrar = (id) => bettyPanel('betty_panel_cierre_borrar', { p_id: id });
export const bettyPanelVacanteGuardar = (v, nueva) => bettyPanel('betty_panel_vacante_guardar', {
  p_clave: v.clave, p_titulo: v.titulo, p_activa: v.activa, p_salario: v.salario, p_sedes: v.sedes,
  p_requisitos: v.requisitos, p_anuncio: v.anuncio, p_correo: v.correo_destino, p_nueva: nueva,
  p_entrevista_dias: v.entrevista_dias || [], // [] = siguiente día hábil
});
export const bettyPanelAplicaciones = (limite = 60) => bettyPanel('betty_panel_aplicaciones', { p_limite: limite });
export const bettyPanelSedeGuardar = (s) => bettyPanel('betty_panel_sede_guardar', {
  p_clave: s.clave, p_nombre: s.nombre, p_direccion: s.direccion, p_latitud: Number(s.latitud), p_longitud: Number(s.longitud),
});
export const bettyPanelInternoGuardar = (i) => bettyPanel('betty_panel_interno_guardar', {
  p_id: i.id || null, p_nombre: i.nombre, p_wa_id: i.wa_id, p_correo: i.correo || null, p_roles: i.roles, p_activo: i.activo,
});
export const bettyPanelConfigGuardar = (campo, valor) => bettyPanel('betty_panel_config_guardar', { p_campo: campo, p_valor: valor });
/** Prueba A/B del primer contacto (Bloque 5): Pases, respuesta y elección por variante. */
export const bettyPruebaPrimerContacto = () => bettyPanel('betty_prueba_primer_contacto', {});
export const bettyPanelBitacora = (limite = 100) => bettyPanel('betty_panel_bitacora', { p_limite: limite });
// Diagnósticos (21-oct-2026): palabras que hacen el trato de Betty «cuidadoso» o «sereno».
export const bettyPanelDiagnosticos = () => bettyPanel('betty_panel_diagnosticos', {});
export const bettyPanelDiagnosticoGuardar = (d) => bettyPanel('betty_panel_diagnostico_guardar', {
  p_id: d.id || null, p_palabra: d.palabra, p_nivel: d.nivel, p_activo: d.activo !== false,
});
export const bettyPanelDiagnosticoBorrar = (id) => bettyPanel('betty_panel_diagnostico_borrar', { p_id: id });
export const bettyPanelDiagnosticoProbar = (texto) => bettyPanel('betty_panel_diagnostico_probar', { p_texto: texto });

/**
 * Plazo que Betty leyó en cada preorden (observación o diagnóstico del médico)
 * y el seguimiento programado. { solicitud_id: {...} }. Lo calcula la base.
 */
export async function getPlazosPreorden(solicitudIds = []) {
  if (!solicitudIds.length) return {};
  const { data, error } = await supabase.rpc('betty_preorden_plazos', { p_ids: solicitudIds });
  if (error) throw error;
  return Object.fromEntries((data || []).map((z) => [z.pase_id, z]));
}

/** Corrige el plazo: fecha en que tocan los laboratorios, o «sin plazo» (vuelve el recordatorio normal). */
export async function corregirPlazoPreorden(solicitudId, fecha, sinPlazo = false) {
  const { data, error } = await supabase.rpc('betty_pase_plazo_corregir', {
    p_pase: solicitudId, p_fecha: sinPlazo ? null : fecha, p_sin_plazo: sinPlazo,
  });
  if (error) throw error;
  return data;
}

/** Cambia el estado lab de una preorden (única vía de escritura del lab). */
export async function marcarPreorden(solicitudId, estado, correlativo = null) {
  const { error } = await supabase.rpc('marcar_preorden', {
    p_solicitud_id: solicitudId, p_estado: estado, p_correlativo: correlativo,
  });
  if (error) throw error;
}

/** Marca la preorden como cotizada (guarda el COT-#### para seguimiento comercial). */
export async function marcarPreordenCotizada(solicitudId, correlativo) {
  const { error } = await supabase.rpc('marcar_preorden_cotizada', {
    p_solicitud_id: solicitudId, p_correlativo: correlativo,
  });
  if (error) throw error;
}

/** 4.1B — Busca UNA solicitud por código SL-#### o UUID, en cualquier estado_lab.
 *  Devuelve el objeto (misma forma que get_preordenes) o null si no existe. */
export async function getSolicitudPorCodigo(codigo) {
  const { data, error } = await supabase.rpc('get_solicitud_por_codigo', { p_codigo: codigo });
  if (error) throw error;
  return data;
}
// ═══════════════════════════════════════════════════════════════════════════════
// CAPA IA — Fase 1 (Edge Function ai-assistant + RPCs de opt-in y decisión)
// ═══════════════════════════════════════════════════════════════════════════════

// Llama al Edge Function con el JWT de la sesión. NO lanza en errores HTTP:
// devuelve { status, data } para que la UI mapee cada portón a un mensaje humano.
export async function llamarAsistenteIA(body) {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) return { status: 401, data: { error: 'no_autenticado' } };
  let res;
  try {
    res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/ai-assistant`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
  } catch {
    return { status: 0, data: { error: 'sin_conexion' } };
  }
  let data = {};
  try { data = await res.json(); } catch { /* respuesta sin cuerpo */ }
  return { status: res.status, data };
}

// Acepta los T&C de la IA. El servidor estampa la versión vigente (el cliente
// nunca envía número de versión).
export async function aceptarTycIA() {
  const { data, error } = await supabase.rpc('aceptar_tyc_ia');
  if (error) throw error;
  return data;
}

// Registra la decisión del médico sobre una sugerencia.
// estado ∈ 'aprobado' | 'rechazado' | 'modificado'; contenido_final es
// obligatorio (lo valida el RPC) al aprobar o modificar.
export async function decidirSugerenciaIA({ interaccionId, estado, contenidoFinal = null }) {
  const { data, error } = await supabase.rpc('decidir_sugerencia_ia', {
    p_interaccion_id: interaccionId,
    p_estado: estado,
    p_contenido_final: contenidoFinal,
  });
  if (error) throw error;
  return data;
}

// Bloque 5 — Sugerencia de exámenes. Es una tarea más del Edge Function
// ai-assistant (mismo router, misma cadena de portones 401→403→503→403→429),
// así que reusa llamarAsistenteIA para heredar el token de sesión y el manejo
// { status, data } sin lanzar. El cliente solo fija la tarea y valida el UUID
// del paciente (mismo 400 que devolvería el servidor). La UI mapea 200/ok,
// sin_protocolos, 422 nota_requerida y los portones a mensajes humanos.
export async function sugerirExamenes({ medPacienteId, consultaId = null } = {}) {
  if (!medPacienteId) return { status: 400, data: { error: 'med_paciente_id_requerido' } };
  const body = { tarea: 'sugerir_examenes', med_paciente_id: medPacienteId };
  if (consultaId) body.consulta_id = consultaId;
  return llamarAsistenteIA(body);
}
// ═══════════════════════════════════════════════════════════════════════════════
// CAPA IA — Bloque 4 (dictado / escriba médico)
// ═══════════════════════════════════════════════════════════════════════════════

// Estado del dictado del médico autenticado (RPC estado_dictado sin argumentos:
// el servidor resuelve la identidad vía get_my_doctor_codigo). Devuelve el jsonb
// {ok, stt_habilitado, max_seg_grabacion, max_seg_dia, usados_hoy, restantes,
// puede_iniciar} o null si el RPC falla — la UI trata null como "no disponible".
export async function estadoDictado() {
  const { data, error } = await supabase.rpc('estado_dictado');
  if (error) return null;
  return data;
}

// Envía un audio (Blob de MediaRecorder) al Edge Function ai-transcribe.
// Mismo patrón que llamarAsistenteIA: NO lanza en errores HTTP, devuelve
// { status, data } para que la UI mapee cada portón a un mensaje humano.
// El audio es EFÍMERO: viaja, se transcribe y se descarta; jamás se guarda.
// OJO: no se fija Content-Type manualmente — el navegador pone el boundary
// del multipart. La duración la mide el SERVIDOR (Whisper), no el cliente.
export async function transcribirAudio(blob, { consultaId = null, medPacienteId = null, nombreArchivo = 'dictado.webm' } = {}) {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) return { status: 401, data: { error: 'no_autenticado' } };
  const form = new FormData();
  form.append('audio', new File([blob], nombreArchivo, { type: blob.type || 'audio/webm' }));
  if (consultaId) form.append('consulta_id', consultaId);
  if (medPacienteId) form.append('med_paciente_id', medPacienteId);
  let res;
  try {
    res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/ai-transcribe`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
  } catch {
    return { status: 0, data: { error: 'sin_conexion' } };
  }
  let data = {};
  try { data = await res.json(); } catch { /* respuesta sin cuerpo */ }
  return { status: res.status, data };
}

// ═══════════════════════════════════════════════════════════════════════════════
// BORRADOR DE NOTA — la red que impide que una nota se pierda
//
// El borrador vive en el SERVIDOR y se ancla al PACIENTE, no a la consulta: la
// consulta puede crearse después, perderse o recrearse, y el borrador
// sobrevive a todo eso. Un borrador abierto por (médico, paciente).
//
// `transcripcion` es el dictado crudo, guardado aparte del texto editado: es lo
// que permite reinsertarlo aunque el médico haya borrado medio texto editando.
// La escribe ai-transcribe server-side, no el navegador.
//
// Motivo: 17-sep-2026 — una nota SOAP completa vivía solo en la memoria del
// navegador mientras la consulta todavía no existía, y se perdió al recrearla.
// ═══════════════════════════════════════════════════════════════════════════════

export async function guardarBorradorNota(medPacienteId, texto, consultaId = null) {
  const { data, error } = await supabase.rpc('guardar_borrador_nota', {
    p_med_paciente_id: medPacienteId,
    p_texto: texto ?? '',
    p_consulta_id: consultaId,
  });
  if (error) throw error;
  return data;
}

export async function leerBorradorNota(medPacienteId) {
  const { data, error } = await supabase.rpc('leer_borrador_nota', {
    p_med_paciente_id: medPacienteId,
  });
  if (error) throw error;
  return data;
}

// Se llama SOLO cuando la nota ya quedó guardada en notas_evolucion. Nada más
// borra un borrador: no expira ni se limpia solo.
export async function descartarBorradorNota(medPacienteId) {
  const { data, error } = await supabase.rpc('descartar_borrador_nota', {
    p_med_paciente_id: medPacienteId,
  });
  if (error) throw error;
  return data;
}

// Parte 4 — Puebla consultas.motivo SOLO si sigue null (.is('motivo', null)):
// el primer guardado de nota gana y el motivo nunca se sobreescribe. Derivación
// determinística en el cliente (primera línea de la nota, sin IA). Devuelve la
// fila actualizada o null si el motivo ya existía (no se tocó nada).
export async function poblarMotivoConsulta(consultaId, motivo) {
  const { data, error } = await supabase
    .from('consultas')
    .update({ motivo })
    .eq('id', consultaId)
    .is('motivo', null)
    .select()
    .maybeSingle();
  if (error) throw error;
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════════
// IMAGENOLOGÍA — estudios de ultrasonido recibidos en el portal
// Bucket privado 'ultrasonidos'. Mismo patrón que adjuntos: Storage primero,
// rollback best-effort, NUNCA URL pública (solo firmadas). Path:
// {medico_codigo}/{estudio_id}/{imagen_id}.{ext} (primer folder = ownership).
// estudios_imagen.med_paciente_id NULL ⇒ está en la BANDEJA (sin asignar).
// Hoy los estudios se registran manualmente; cuando se conecten los equipos
// por DICOM, el gateway insertará en estas mismas tablas.
// ═══════════════════════════════════════════════════════════════════════════════

const US_BUCKET = 'ultrasonidos';
const US_MAX_BYTES = 20 * 1024 * 1024; // 20 MB
const US_MIME_EXT = { 'image/jpeg': 'jpg', 'image/png': 'png', 'image/webp': 'webp' };

/**
 * Registra un estudio con sus imágenes. Si med_paciente_id es null,
 * el estudio cae a la bandeja de "pendientes de asignar".
 * @param {File[]} files - imágenes del estudio (JPG/PNG/WEBP)
 * @param {Object} opts { descripcion, equipo, med_paciente_id, medico_codigo }
 * @returns {Promise<Object>} la fila de estudios_imagen
 */
export async function crearEstudioImagen(files, { descripcion = null, equipo = null, med_paciente_id = null, medico_codigo } = {}) {
  if (!files?.length) throw new Error('No se recibieron imágenes.');
  for (const f of files) {
    if (!US_MIME_EXT[f.type]) throw new Error(`"${f.name}": tipo no permitido. Solo JPG, PNG o WEBP.`);
    if (f.size <= 0) throw new Error(`"${f.name}": el archivo está vacío.`);
    if (f.size > US_MAX_BYTES) throw new Error(`"${f.name}": supera el límite de 20 MB.`);
  }
  const codigo = await _resolverCodigoMedico(medico_codigo);

  // 1) Fila del estudio
  const { data: estudio, error: eErr } = await supabase
    .from('estudios_imagen')
    .insert({ medico_codigo: codigo, med_paciente_id, descripcion, equipo, origen: 'manual' })
    .select()
    .single();
  if (eErr) throw new Error('Error al registrar el estudio: ' + eErr.message);

  // 2) Imágenes: Storage primero, luego fila (rollback best-effort por imagen)
  const subidas = [];
  try {
    let orden = 0;
    for (const f of files) {
      const imgId = crypto.randomUUID();
      const path = `${codigo}/${estudio.id}/${imgId}.${US_MIME_EXT[f.type]}`;
      const { error: upErr } = await supabase.storage
        .from(US_BUCKET)
        .upload(path, f, { contentType: f.type, upsert: false });
      if (upErr) throw new Error('Error al subir "' + f.name + '": ' + upErr.message);
      const { error: iErr } = await supabase
        .from('estudio_imagenes')
        .insert({ id: imgId, estudio_id: estudio.id, medico_codigo: codigo, storage_path: path, mime_type: f.type, size_bytes: f.size, orden: orden++ });
      if (iErr) { await supabase.storage.from(US_BUCKET).remove([path]); throw new Error('Error al registrar imagen: ' + iErr.message); }
      subidas.push(path);
    }
  } catch (e) {
    // Rollback total best-effort: objetos + estudio (cascade borra filas de imágenes)
    if (subidas.length) await supabase.storage.from(US_BUCKET).remove(subidas);
    await supabase.from('estudios_imagen').delete().eq('id', estudio.id);
    throw e;
  }
  return estudio;
}

/**
 * Lista estudios del médico con sus imágenes y el paciente asignado (si hay).
 * @param {Object} opts
 * @param {string|null} [opts.med_paciente_id] - solo los de ese paciente
 * @param {boolean} [opts.pendientes] - solo los sin asignar (bandeja)
 */
export async function listarEstudiosImagen({ med_paciente_id = null, pendientes = false } = {}) {
  let q = supabase
    .from('estudios_imagen')
    .select('id, med_paciente_id, modalidad, descripcion, equipo, origen, fecha_estudio, created_at, estudio_imagenes(id, storage_path, mime_type, orden), med_pacientes(id, nombre, apellido)')
    .order('created_at', { ascending: false });
  if (med_paciente_id) q = q.eq('med_paciente_id', med_paciente_id);
  else if (pendientes) q = q.is('med_paciente_id', null);
  const { data, error } = await q;
  if (error) throw new Error('Error al listar estudios: ' + error.message);
  // Imágenes ordenadas
  return (data || []).map(e => ({
    ...e,
    estudio_imagenes: (e.estudio_imagenes || []).sort((a, b) => a.orden - b.orden),
  }));
}

/** Asigna (o reasigna) un estudio de la bandeja al expediente de un paciente. */
export async function asignarEstudioImagen(estudio_id, med_paciente_id) {
  const { data, error } = await supabase
    .from('estudios_imagen')
    .update({ med_paciente_id })
    .eq('id', estudio_id)
    .select('id, med_paciente_id')
    .maybeSingle();
  if (error) throw new Error('Error al asignar el estudio: ' + error.message);
  if (!data) throw new Error('No se pudo asignar: el estudio o el paciente no te pertenecen.');
  return data;
}

/** URL firmada de vida corta para ver una imagen (el bucket es privado). */
export async function getUltrasonidoUrl(storage_path, expiresIn = 60) {
  if (!storage_path) throw new Error('Falta storage_path.');
  const { data, error } = await supabase.storage
    .from(US_BUCKET)
    .createSignedUrl(storage_path, expiresIn);
  if (error) throw new Error('Error al firmar URL: ' + error.message);
  return data.signedUrl;
}

// ── Canal LABORATORIO → MÉDICO ────────────────────────────────────────────────
// El staff del lab (admin/qb/op) envía un estudio al médico referente:
// medico_codigo = destino, origen='laboratorio', med_paciente_id NULL.
// El estudio cae a la bandeja "Estudios recibidos" del médico y ÉL lo asigna
// al expediente. paciente_ref = nombre del paciente según el lab (pista).

/**
 * Envía un estudio de imagen del laboratorio a un médico.
 * @param {File[]} files - imágenes (JPG/PNG/WEBP)
 * @param {Object} opts { medico_codigo (destino, obligatorio), paciente_ref,
 *                        descripcion, equipo, enviado_por (username del staff) }
 */
export async function enviarEstudioLabAMedico(files, { medico_codigo, paciente_ref = null, descripcion = null, equipo = null, enviado_por = null } = {}) {
  if (!files?.length) throw new Error('No se recibieron imágenes.');
  if (!medico_codigo) throw new Error('Falta el médico destino.');
  for (const f of files) {
    if (!US_MIME_EXT[f.type]) throw new Error(`"${f.name}": tipo no permitido. Solo JPG, PNG o WEBP.`);
    if (f.size <= 0) throw new Error(`"${f.name}": el archivo está vacío.`);
    if (f.size > US_MAX_BYTES) throw new Error(`"${f.name}": supera el límite de 20 MB.`);
  }

  const { data: estudio, error: eErr } = await supabase
    .from('estudios_imagen')
    .insert({ medico_codigo, med_paciente_id: null, descripcion, equipo, origen: 'laboratorio', paciente_ref, enviado_por })
    .select()
    .single();
  if (eErr) throw new Error('Error al registrar el envío: ' + eErr.message);

  const subidas = [];
  try {
    let orden = 0;
    for (const f of files) {
      const imgId = crypto.randomUUID();
      const path = `${medico_codigo}/${estudio.id}/${imgId}.${US_MIME_EXT[f.type]}`;
      const { error: upErr } = await supabase.storage
        .from(US_BUCKET)
        .upload(path, f, { contentType: f.type, upsert: false });
      if (upErr) throw new Error('Error al subir "' + f.name + '": ' + upErr.message);
      const { error: iErr } = await supabase
        .from('estudio_imagenes')
        .insert({ id: imgId, estudio_id: estudio.id, medico_codigo, storage_path: path, mime_type: f.type, size_bytes: f.size, orden: orden++ });
      if (iErr) { await supabase.storage.from(US_BUCKET).remove([path]); throw new Error('Error al registrar imagen: ' + iErr.message); }
      subidas.push(path);
    }
  } catch (e) {
    if (subidas.length) await supabase.storage.from(US_BUCKET).remove(subidas);
    await supabase.from('estudios_imagen').delete().eq('id', estudio.id);
    throw e;
  }
  return estudio;
}

/** Bandeja de salida del laboratorio: todo lo enviado, con estado de asignación. */
export async function listarEstudiosLab() {
  const { data, error } = await supabase
    .from('estudios_imagen')
    .select('id, medico_codigo, med_paciente_id, modalidad, descripcion, equipo, origen, paciente_ref, enviado_por, fecha_estudio, created_at, estudio_imagenes(id, storage_path, mime_type, orden)')
    .eq('origen', 'laboratorio')
    .order('created_at', { ascending: false });
  if (error) throw new Error('Error al listar envíos: ' + error.message);
  return (data || []).map(e => ({
    ...e,
    estudio_imagenes: (e.estudio_imagenes || []).sort((a, b) => a.orden - b.orden),
  }));
}

/** Borra un estudio completo (filas por cascade; objetos best-effort). */
export async function borrarEstudioImagen(estudio) {
  if (!estudio?.id) throw new Error('Estudio inválido.');
  const paths = (estudio.estudio_imagenes || []).map(i => i.storage_path).filter(Boolean);
  const { data, error } = await supabase
    .from('estudios_imagen')
    .delete()
    .eq('id', estudio.id)
    .select('id');
  if (error) throw new Error('Error al borrar el estudio: ' + error.message);
  if (!data || data.length === 0) throw new Error('No se pudo borrar: no estás autorizado.');
  if (paths.length) await supabase.storage.from(US_BUCKET).remove(paths);
}

// ═══════════════════════════════════════════════════════════════════════════════
// GERENCIA (fase 3) — indicadores, descuentos, personal y bitácora del manager
//
// Todo pasa por RPC, nunca por SELECT directo a las tablas. No es capricho de
// estilo: el manager tiene prohibido el contenido clínico, y la única forma de
// garantizarlo es que la base devuelva agregados y metadatos ya recortados. Si
// esta capa leyera `consultas` o `records` en crudo, la frontera se caería en el
// primer reporte que alguien pida.
//
// Cada RPC valida su propio permiso del catálogo (`gerencia.*`) y acota por la
// institución que gestiona quien llama. Un manager sin permiso recibe una
// excepción de la base, no una lista vacía: el error se muestra tal cual.
// ═══════════════════════════════════════════════════════════════════════════════

// Las fechas viajan como 'YYYY-MM-DD'. `null` deja que el servidor elija su
// ventana por defecto (últimos 30 días), que es lo que quiere el tablero al abrir.
const ymd = (v) => {
  if (!v) return null;
  if (typeof v === 'string') return v.slice(0, 10);
  return new Date(v).toLocaleDateString('en-CA', { timeZone: 'America/Guatemala' });
};

/**
 * Tablero gerencial: agenda, laboratorio, finanzas y equipo de la organización.
 *
 * El bloque `finanzas` llega como `{ oculto: true }` cuando el perfil no tiene
 * `gerencia.finanzas.ver`. Es a propósito: el servidor decide qué se ve, así la
 * pantalla no tiene que adivinarlo ni pedir los números para después esconderlos.
 */
export async function getGerenciaDashboard({ desde = null, hasta = null } = {}) {
  const { data, error } = await supabase.rpc('get_gerencia_dashboard', {
    p_desde: ymd(desde), p_hasta: ymd(hasta),
  });
  if (error) throw error;
  return data || null;
}

/**
 * Productividad por médico, identificados con nombre.
 *
 * Devuelve conteos y montos: citas, cumplidas, inasistencias, consultas cerradas
 * y órdenes de laboratorio. Nunca el motivo de consulta ni el contenido clínico.
 */
export async function getProductividadMedicos({ desde = null, hasta = null } = {}) {
  const { data, error } = await supabase.rpc('get_productividad_medicos', {
    p_desde: ymd(desde), p_hasta: ymd(hasta),
  });
  if (error) throw error;
  return data || [];
}

/**
 * Agenda consolidada de la organización: todos los médicos en una sola vista.
 *
 * Trae horario, duración, médico y estado. Paciente, teléfono y motivo llegan en
 * `null` salvo que el perfil tenga `gerencia.agenda.pacientes` — el manager mide
 * ocupación por defecto, y ver a quién atiende cada médico se concede aparte.
 * `puede_editar` refleja `gerencia.agenda.gestionar`: la pantalla lo usa para
 * habilitar el arrastre en vez de dejar que la escritura rebote contra el trigger.
 *
 * Notas de la cita nunca viajan acá, tenga el permiso que tenga.
 */
export async function getAgendaGerencia({ desde = null, hasta = null } = {}) {
  const { data, error } = await supabase.rpc('get_agenda_gerencia', {
    p_desde: ymd(desde), p_hasta: ymd(hasta),
  });
  if (error) throw error;
  return data || [];
}

/** Descuentos que superaron el umbral y esperan autorización. */
export async function getDescuentosPendientes(limite = 100) {
  const { data, error } = await supabase.rpc('get_descuentos_pendientes', { p_limit: limite });
  if (error) throw error;
  return data || [];
}

/**
 * Autoriza o rechaza un descuento pendiente.
 *
 * La base exige `gerencia.finanzas.descuentos`, que el descuento siga pendiente
 * y que sea de la organización de quien resuelve. Un segundo clic sobre el mismo
 * descuento rebota con `descuento_ya_resuelto`, no lo pisa.
 */
export async function resolverDescuento({ id, aprobado, motivo = null }) {
  if (!id) throw new Error('Descuento inválido.');
  const { data, error } = await supabase.rpc('resolver_descuento', {
    p_id: id, p_aprobado: !!aprobado, p_motivo: motivo,
  });
  if (error) throw error;
  return data;
}

/** Umbrales vigentes (porcentaje y monto) para una institución o los globales. */
export async function getUmbralDescuento(institucionId = null) {
  const { data, error } = await supabase.rpc('umbral_descuento', { p_institucion_id: institucionId });
  if (error) throw error;
  return (data && data[0]) || null;
}

/**
 * Bitácora de la organización: qué tabla, quién y qué campos cambiaron.
 *
 * `cambios` es la lista de nombres de campo. El contenido no viaja nunca —
 * `fn_auditar` ya excluye alergias, datos críticos, notas y DPI en el origen.
 */
export async function getAuditoriaGerencia({ desde = null, hasta = null, limite = 200 } = {}) {
  const { data, error } = await supabase.rpc('get_auditoria_gerencia', {
    p_desde: ymd(desde), p_hasta: ymd(hasta), p_limit: limite,
  });
  if (error) throw error;
  return data || [];
}

/**
 * Personal administrativo de la organización con su estado real.
 *
 * `administrable` lo calcula la base perfil por perfil: si viene en false, el
 * botón de baja se muestra apagado en vez de dejar que el usuario lo pulse y
 * coma un error. Un manager nunca ve `administrable` sobre médicos ni admins.
 */
export async function getPersonalGerencia() {
  const { data, error } = await supabase.rpc('get_personal_gerencia');
  if (error) throw error;
  return data || [];
}

/** Da de baja a un perfil administrativo y revoca sus agendas asignadas. */
export async function desactivarPersonal(perfilId, motivo = null) {
  const { data, error } = await supabase.rpc('desactivar_personal', {
    p_perfil_id: perfilId, p_motivo: motivo,
  });
  if (error) throw error;
  return data;
}

/**
 * Reactiva un perfil dado de baja.
 *
 * Las agendas revocadas NO vuelven solas: se reasignan a mano. Es deliberado —
 * que alguien regrese no significa que deba recuperar los mismos accesos.
 */
export async function reactivarPersonal(perfilId) {
  const { data, error } = await supabase.rpc('reactivar_personal', { p_perfil_id: perfilId });
  if (error) throw error;
  return data;
}

// ── Grupos de cobertura ───────────────────────────────────────────────────────
// Permiten que varios médicos de una misma institución compartan expediente en
// SOLO LECTURA. Los arma el manager, y armarlos no le da a él acceso clínico:
// administra la membresía, sigue sin poder abrir una nota.

/** Grupos de la organización, cada uno con sus médicos activos. */
export async function getGruposCobertura() {
  const { data, error } = await supabase.rpc('get_grupos_cobertura');
  if (error) throw error;
  return data || [];
}

export async function crearGrupoCobertura(nombre, descripcion = null) {
  const { data, error } = await supabase.rpc('crear_grupo_cobertura', {
    p_nombre: nombre, p_descripcion: descripcion,
  });
  if (error) throw error;
  return data;
}

export async function actualizarGrupoCobertura(
  grupoId, { nombre = null, descripcion = null, activo = null } = {}) {
  const { data, error } = await supabase.rpc('actualizar_grupo_cobertura', {
    p_grupo_id: grupoId, p_nombre: nombre, p_descripcion: descripcion, p_activo: activo,
  });
  if (error) throw error;
  return data;
}

export async function agregarMedicoGrupo(grupoId, doctorId) {
  const { data, error } = await supabase.rpc('agregar_medico_grupo', {
    p_grupo_id: grupoId, p_doctor_id: doctorId,
  });
  if (error) throw error;
  return data;
}

/** Saca a un médico del grupo. Se revoca, no se borra: queda el rastro. */
export async function quitarMedicoGrupo(grupoId, doctorId) {
  const { data, error } = await supabase.rpc('quitar_medico_grupo', {
    p_grupo_id: grupoId, p_doctor_id: doctorId,
  });
  if (error) throw error;
  return data;
}

/**
 * Con qué colegas comparte expediente el médico que llama.
 *
 * Devuelve `[{ codigo, doctor, grupo }]`. La pantalla lo necesita para marcar
 * qué pacientes son prestados y de quién: sin esa marca, el expediente de otro
 * médico se mezclaría con el propio sin que se note, que es exactamente el
 * error que no queremos en un historial clínico.
 */
export async function getMiCobertura() {
  const { data, error } = await supabase.rpc('get_mi_cobertura');
  if (error) { console.error('getMiCobertura error:', error); return []; }
  return data || [];
}

// ═══════════════════════════════════════════════════════════════════════════════
// CAJA — cuentas por cobrar del paciente, abonos parciales y corte de turno
//
// Por qué existe: muchos pacientes pagan un procedimiento poco a poco. Antes eso
// vivía en un cuaderno. Acá una cuenta tiene monto total, N abonos con fecha y un
// saldo que NO se guarda — se deriva de los abonos vigentes, así no puede quedar
// desincronizado con lo que realmente entró.
//
// Toda escritura pasa por RPC. `caja_cuentas`, `caja_abonos` y `caja_turnos` solo
// conceden SELECT a `authenticated`: no hay forma de insertar un abono saltándose
// la validación de saldo, de permiso o de turno abierto. Si esta capa escribiera
// por PostgREST, la primera cajera con la consola abierta sobregira una cuenta.
//
// Quién ve qué lo decide la base: la secretaria, solo los médicos que tiene
// asignados y solo si el manager le encendió `caja.*`; el manager, toda su
// organización con `gerencia.caja.ver`.
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Cartera de cuentas visible para quien llama, ya con saldo calculado.
 *
 * Ordena primero las abiertas: la pantalla de caja se usa para cobrar, no para
 * revisar historia. `estado` filtra 'abierta' | 'liquidada' | 'cancelada';
 * `busqueda` cruza nombre de paciente y concepto.
 */
export async function getCajaCuentas({
  medicoCodigo = null, estado = null, busqueda = null, limite = 200,
} = {}) {
  const { data, error } = await supabase.rpc('get_caja_cuentas', {
    p_medico_codigo: medicoCodigo,
    p_estado: estado,
    p_busqueda: busqueda,
    p_limit: limite,
  });
  if (error) throw error;
  return data || [];
}

/**
 * Estado de cuenta completo: cabecera, saldo y los abonos con su recibo.
 *
 * Incluye los anulados, marcados con `anulado: true` y su motivo. Se muestran a
 * propósito — un recibo que desaparece de la pantalla es un recibo que el
 * paciente sigue teniendo en la mano.
 */
export async function getCajaCuenta(cuentaId) {
  if (!cuentaId) return null;
  const { data, error } = await supabase.rpc('get_caja_cuenta', { p_cuenta_id: cuentaId });
  if (error) throw error;
  return data || null;
}

/**
 * Abre la cuenta de un procedimiento y fija su monto total.
 *
 * `citaId` y `consultaId` son opcionales y enlazan el cobro con su origen. Sin
 * ese enlace no hay forma de preguntar qué citas cumplidas nunca se cobraron:
 * la fuga de ingresos queda invisible. La base valida que la cita sea del mismo
 * paciente y del mismo médico, así que un enlace equivocado revienta acá y no
 * se convierte en un reporte con basura. Si se manda `consultaId` sin `citaId`,
 * la base hereda la cita de la consulta.
 */
export async function crearCuentaCaja({
  medPacienteId, concepto, montoTotal, notas = null,
  citaId = null, consultaId = null,
}) {
  const { data, error } = await supabase.rpc('crear_cuenta_caja', {
    p_med_paciente_id: medPacienteId,
    p_concepto: concepto,
    p_monto_total: montoTotal,
    p_notas: notas,
    p_cita_id: citaId,
    p_consulta_id: consultaId,
  });
  if (error) throw error;
  return data;
}

/**
 * Atenciones que no tienen ningún cobro registrado.
 *
 * Una atención es una cita cumplida, o una consulta cerrada que no salió de una
 * cita cumplida — el paciente que llegó sin agendar. Las dos cuentan: en
 * producción, 12 de las consultas cerradas no tienen cita, y antes eran
 * invisibles para este reporte.
 *
 * La misma función sirve a los tres roles y el alcance lo pone la base, no este
 * parámetro: el manager ve su organización, la secretaria los médicos que tiene
 * asignados, el médico los suyos. Por eso no hay que filtrar acá.
 *
 * Cada fila trae `cita_id` y `consulta_id`, que es lo que permite abrir la
 * cuenta ya enlazada con su origen. No devuelve el motivo de la cita a
 * propósito: para perseguir un cobro hace falta saber a quién llamar, no por
 * qué vino.
 *
 * El rango tiene tope de 400 días y la lista tope de 1000 filas, ambos en la
 * base.
 */
export async function getAtencionesSinCobro({ desde = null, hasta = null, limite = 200 } = {}) {
  const { data, error } = await supabase.rpc('get_atenciones_sin_cobro', {
    p_desde: ymd(desde), p_hasta: ymd(hasta), p_limit: limite,
  });
  if (error) throw error;
  return data || [];
}

/**
 * Registra un abono y devuelve el estado de cuenta actualizado.
 *
 * El nivel superior trae además `recibo_no`, el correlativo del abono recién
 * recibido, que es lo que se imprime. La base rechaza cobrar más que el saldo y
 * exige turno abierto cuando el método es efectivo: sin eso el corte no cuadra.
 */
export async function registrarAbonoCaja({
  cuentaId, monto, metodo = 'efectivo', referencia = null, nota = null,
}) {
  const { data, error } = await supabase.rpc('registrar_abono_caja', {
    p_cuenta_id: cuentaId,
    p_monto: monto,
    p_metodo: metodo,
    p_referencia: referencia,
    p_nota: nota,
  });
  if (error) throw error;
  return data;
}

/**
 * Anula un abono mal capturado. El motivo es obligatorio y queda en la bitácora.
 *
 * No borra la fila: la cuenta vuelve a 'abierta' y el recibo queda visible como
 * anulado. Un abono de un turno ya cerrado no se puede tocar — ese corte ya se
 * arqueó y moverlo por detrás dejaría el arqueo mintiendo.
 */
export async function anularAbonoCaja(abonoId, motivo) {
  const { data, error } = await supabase.rpc('anular_abono_caja', {
    p_abono_id: abonoId, p_motivo: motivo,
  });
  if (error) throw error;
  return data;
}

/** Cambia monto total o concepto. La base impide bajarlo de lo ya abonado. */
export async function ajustarCuentaCaja({ cuentaId, montoTotal = null, concepto = null, motivo = null }) {
  const { data, error } = await supabase.rpc('ajustar_cuenta_caja', {
    p_cuenta_id: cuentaId,
    p_monto_total: montoTotal,
    p_concepto: concepto,
    p_motivo: motivo,
  });
  if (error) throw error;
  return data;
}

/** Cancela una cuenta sin movimientos. Con dinero recibido la base la rebota. */
export async function cancelarCuentaCaja(cuentaId, motivo) {
  const { data, error } = await supabase.rpc('cancelar_cuenta_caja', {
    p_cuenta_id: cuentaId, p_motivo: motivo,
  });
  if (error) throw error;
  return data;
}

// ── Turno de caja ─────────────────────────────────────────────────────────────

/** Abre el turno con el fondo inicial. Solo uno abierto por persona. */
export async function abrirTurnoCaja({ fondoApertura = 0, notas = null } = {}) {
  const { data, error } = await supabase.rpc('abrir_turno_caja', {
    p_fondo_apertura: fondoApertura, p_notas: notas,
  });
  if (error) throw error;
  return data;
}

/**
 * Turno abierto de quien llama, con lo cobrado hasta el momento.
 *
 * Devuelve `{ abierto: false }` cuando no hay ninguno — no es un error, es el
 * estado normal antes de empezar el día.
 */
export async function getMiTurnoCaja() {
  const { data, error } = await supabase.rpc('get_mi_turno_caja');
  if (error) throw error;
  return data || { abierto: false };
}

/**
 * Cierra el turno con el efectivo contado y devuelve el corte.
 *
 * `diferencia` positiva es sobrante y negativa faltante, contra
 * `fondo_apertura + efectivo cobrado`. No se bloquea el cierre por descuadre: se
 * registra. Impedirlo solo lograría que nadie cierre la caja.
 */
export async function cerrarTurnoCaja({ efectivoDeclarado, notas = null }) {
  const { data, error } = await supabase.rpc('cerrar_turno_caja', {
    p_efectivo_declarado: efectivoDeclarado, p_notas: notas,
  });
  if (error) throw error;
  return data;
}

/** Corte de un turno. Sin id, el turno abierto de quien llama. */
export async function getCorteCaja(turnoId = null) {
  const { data, error } = await supabase.rpc('get_corte_caja', { p_turno_id: turnoId });
  if (error) throw error;
  return data || null;
}

// ── Vista de gerencia ─────────────────────────────────────────────────────────

/** Cortes de todas las cajas de la organización. Exige `gerencia.caja.ver`. */
export async function getCortesCaja({ desde = null, hasta = null } = {}) {
  const { data, error } = await supabase.rpc('get_cortes_caja', {
    p_desde: ymd(desde), p_hasta: ymd(hasta),
  });
  if (error) throw error;
  return data || [];
}

/**
 * Resumen de caja de la organización: cobrado por método, cartera y por médico.
 *
 * `cobrado` se acota a la ventana pedida; `cartera` es el saldo vivo completo, no
 * el del rango — lo que el paciente debe no caduca porque cambies el filtro.
 */
export async function getCajaResumen({ desde = null, hasta = null } = {}) {
  const { data, error } = await supabase.rpc('get_caja_resumen', {
    p_desde: ymd(desde), p_hasta: ymd(hasta),
  });
  if (error) throw error;
  return data || null;
}

// ═══════════════════════════════════════════════════════════════════════════════
// GOOGLE CALENDAR — conexión del médico
// Sincronización unidireccional: el portal escribe en un calendario propio
// ("Innovalab — Consultas") dentro de la cuenta de Google del médico. Nunca
// leemos su agenda ni tocamos su calendario personal: el scope calendar.app.created
// solo alcanza calendarios creados por esta app.
// ═══════════════════════════════════════════════════════════════════════════════

const GOOGLE_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  '107974648981-pun6hg0kgcmlnbl4tk638ak4fi9qsq07.apps.googleusercontent.com';

// La dirección de retorno usa el dominio propio, no el de Supabase: es lo que
// Google le muestra al médico en la pantalla de consentimiento. Vercel reenvía
// /api/google/callback a la Edge Function (ver vercel.json).
const GOOGLE_REDIRECT = 'https://innovalab.scgrupomedico.com/api/google/callback';

const GOOGLE_SCOPES = [
  'openid',
  'email',
  'https://www.googleapis.com/auth/calendar.app.created',
].join(' ');

// Estado de la conexión del médico en sesión. Nunca expone el token.
export async function getGoogleEstado() {
  const { data, error } = await supabase.rpc('mi_google_estado');
  if (error) {
    console.error('getGoogleEstado:', error);
    return { conectado: false };
  }
  const fila = Array.isArray(data) ? data[0] : data;
  return fila || { conectado: false };
}

// Manda al médico a la pantalla de consentimiento de Google.
// El `state` lo emite la base y vale 15 minutos: es lo que le permite al
// callback saber qué médico volvió sin confiar en nada del navegador.
export async function iniciarConexionGoogle() {
  const { data: state, error } = await supabase.rpc('crear_google_oauth_state');
  if (error) throw error;

  const url = new URL('https://accounts.google.com/o/oauth2/v2/auth');
  url.searchParams.set('client_id', GOOGLE_CLIENT_ID);
  url.searchParams.set('redirect_uri', GOOGLE_REDIRECT);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('scope', GOOGLE_SCOPES);
  url.searchParams.set('access_type', 'offline');
  url.searchParams.set('prompt', 'consent');   // fuerza refresh_token
  url.searchParams.set('state', state);

  window.location.href = url.toString();
}

export async function desconectarGoogle() {
  const { error } = await supabase.rpc('desconectar_google');
  if (error) throw error;
  return true;
}

/* ────────────────────────────────────────────────────────────────────────────
   Ciclos de fertilidad (módulo fertilidad.ciclos)
   ──────────────────────────────────────────────────────────────────────────── */

export async function getCiclos({ medPacienteId = null, limite = 20, offset = 0 } = {}) {
  const { data, error } = await supabase.rpc('get_ciclos', {
    p_med_paciente_id: medPacienteId || null,
    p_limite: limite,
    p_offset: offset,
  });
  if (error) throw error;
  const rows = data || [];
  const total = rows.length ? Number(rows[0].total) : 0;
  return { rows, total };
}

export async function getCicloCompleto(cicloId) {
  const { data, error } = await supabase.rpc('get_ciclo_completo', { p_ciclo_id: cicloId });
  if (error) throw error;
  return data || null;
}

export async function crearCiclo({ medPacienteId, plantillaClave, fechaInicio = null,
                                   fur = null, parejaId = null, encabezado = {},
                                   diaInicio = 1 }) {
  const { data, error } = await supabase.rpc('crear_ciclo', {
    p_med_paciente_id: medPacienteId,
    p_plantilla_clave: plantillaClave,
    p_fecha_inicio: fechaInicio || null,
    p_fur: fur || null,
    p_pareja_id: parejaId || null,
    p_encabezado: encabezado || {},
    p_dia_inicio: diaInicio || 1,
  });
  if (error) throw error;
  return data;
}

// Calendario del ciclo: la FUR es el dia 1 y la hoja arranca en el dia que
// elija el medico. Cambiar cualquiera de los dos recalcula la fecha de inicio.
export async function setCicloCalendario({ cicloId, fur = null, diaInicio = null }) {
  const { data, error } = await supabase.rpc('set_ciclo_calendario', {
    p_ciclo_id: cicloId, p_fur: fur || null, p_dia_inicio: diaInicio || null,
  });
  if (error) throw error;
  return data;
}

// Filas de medicamento que el medico agrega en su propio ciclo.
export async function agregarFilaCiclo({ cicloId, etiqueta, unidad = null, grupo = 'medicamento' }) {
  const { data, error } = await supabase.rpc('agregar_fila_ciclo', {
    p_ciclo_id: cicloId, p_etiqueta: etiqueta, p_unidad: unidad || null, p_grupo: grupo,
  });
  if (error) throw error;
  return data;
}

export async function renombrarFilaCiclo({ cicloId, filaId, etiqueta, unidad = null }) {
  const { data, error } = await supabase.rpc('renombrar_fila_ciclo', {
    p_ciclo_id: cicloId, p_fila_id: filaId, p_etiqueta: etiqueta, p_unidad: unidad || null,
  });
  if (error) throw error;
  return data;
}

export async function quitarFilaCiclo({ cicloId, filaId }) {
  const { data, error } = await supabase.rpc('quitar_fila_ciclo', {
    p_ciclo_id: cicloId, p_fila_id: filaId,
  });
  if (error) throw error;
  return data;
}

// Fila con dos medicamentos (Hucog / Ovidrel): cual o cuales se usan en este
// ciclo. Se permite mas de uno: es raro, pero pasa (faltante de un producto).
export async function setCicloFilaOpcion({ cicloId, filaId, opciones }) {
  const { data, error } = await supabase.rpc('set_ciclo_fila_opcion', {
    p_ciclo_id: cicloId, p_fila_id: filaId,
    p_opciones: Array.isArray(opciones) && opciones.length ? opciones : null,
  });
  if (error) throw error;
  return data;
}

export async function setCicloValor({ cicloId, dia, campoId, valorNum = null, valorTexto = null }) {
  const { data, error } = await supabase.rpc('set_ciclo_valor', {
    p_ciclo_id: cicloId,
    p_dia: dia,
    p_campo_id: campoId,
    p_valor_num: valorNum,
    p_valor_texto: valorTexto,
  });
  if (error) throw error;
  return data;
}

export async function setCicloFoliculos({ cicloId, dia, ovario, mediciones }) {
  const { data, error } = await supabase.rpc('set_ciclo_foliculos', {
    p_ciclo_id: cicloId,
    p_dia: dia,
    p_ovario: ovario,
    p_mediciones: mediciones || [],
  });
  if (error) throw error;
  return data;
}

export async function setCicloDia({ cicloId, dia, fecha = null, observacion = null }) {
  const { data, error } = await supabase.rpc('set_ciclo_dia', {
    p_ciclo_id: cicloId, p_dia: dia, p_fecha: fecha || null, p_observacion: observacion,
  });
  if (error) throw error;
  return data;
}

// Encabezado de la hoja: se guarda campo por campo (y subcampo para los de tipo
// 'objeto'), así dos personas editando campos distintos no se pisan.
export async function setCicloEncabezado({ cicloId, campoId, valor, subcampoId = null }) {
  const { data, error } = await supabase.rpc('set_ciclo_encabezado', {
    p_ciclo_id: cicloId, p_campo_id: campoId,
    p_valor: valor === undefined ? null : valor, p_subcampo_id: subcampoId,
  });
  if (error) throw error;
  return data;
}

// Pie de la hoja (hitos): un campo o la fecha de una sección.
export async function setCicloHitoCampo({ cicloId, tipo, campoId, valor }) {
  const { data, error } = await supabase.rpc('set_ciclo_hito_campo', {
    p_ciclo_id: cicloId, p_tipo: tipo, p_campo_id: campoId, p_valor: valor ?? null,
  });
  if (error) throw error;
  return data;
}

export async function setCicloHitoFecha({ cicloId, tipo, fecha }) {
  const { data, error } = await supabase.rpc('set_ciclo_hito_fecha', {
    p_ciclo_id: cicloId, p_tipo: tipo, p_fecha: fecha || null,
  });
  if (error) throw error;
  return data;
}

export async function agregarNotaCiclo({ cicloId, contenido, dia = null }) {
  const { data, error } = await supabase.rpc('agregar_nota_ciclo', {
    p_ciclo_id: cicloId, p_contenido: contenido, p_dia: dia,
  });
  if (error) throw error;
  return data;
}

// Reconcilia el ciclo contra los resultados aprobados del laboratorio y los
// estudios de imagen del paciente. Es idempotente: se puede llamar las veces
// que sea. Devuelve el conteo de lo que escribió.
export async function sincronizarLabCiclo(cicloId) {
  const { data, error } = await supabase.rpc('fert_sincronizar_lab', { p_ciclo_id: cicloId });
  if (error) throw error;
  return data;
}

export async function cambiarEstadoCiclo({ cicloId, estado, motivo = null }) {
  const { data, error } = await supabase.rpc('cambiar_estado_ciclo', {
    p_ciclo_id: cicloId, p_estado: estado, p_motivo: motivo,
  });
  if (error) throw error;
  return data;
}

// ═══════════════════════════════════════════════════════════════════════════
// CRM COMERCIAL (28-sep-2026) — cuentas, proyectos y actividad.
// La RLS exige staff + permiso `crm.comercial` (admin siempre). La salud de la
// cuenta y el comparativo por responsable se calculan en la base, nunca aquí.
// ═══════════════════════════════════════════════════════════════════════════

export async function crmListarCuentas() {
  const { data, error } = await supabase.from('crm_cuentas').select('*').order('nombre');
  if (error) throw error;
  return data || [];
}

export async function crmSaludCuentas() {
  const { data, error } = await supabase.rpc('crm_salud_cuentas');
  if (error) throw error;
  return data || [];
}

export async function crmGuardarCuenta(cuenta) {
  const { id, created_at, created_by, updated_at, updated_by, ...campos } = cuenta;
  const q = id
    ? supabase.from('crm_cuentas').update(campos).eq('id', id).select().single()
    : supabase.from('crm_cuentas').insert(campos).select().single();
  const { data, error } = await q;
  if (error) throw error;
  return data;
}

export async function crmListarProyectos() {
  const { data, error } = await supabase.from('crm_proyectos').select('*').order('updated_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

export async function crmGuardarProyecto(proyecto) {
  // cerrado_at lo sella la base al mover la etapa. Solo al CARGAR un proyecto
  // que ya venía cerrado se manda su fecha real (cerrado_real, 'aaaa-mm-dd').
  const { id, created_at, created_by, updated_at, updated_by, etapa_desde, cerrado_at, cerrado_real, ...campos } = proyecto;
  if (!id && cerrado_real) campos.cerrado_at = `${cerrado_real}T12:00:00-06:00`;
  const q = id
    ? supabase.from('crm_proyectos').update(campos).eq('id', id).select().single()
    : supabase.from('crm_proyectos').insert(campos).select().single();
  const { data, error } = await q;
  if (error) throw error;
  return data;
}

export async function crmActividadesCuenta(cuentaId, limite = 100) {
  const { data, error } = await supabase.from('crm_actividades')
    .select('*').eq('cuenta_id', cuentaId).order('fecha', { ascending: false }).limit(limite);
  if (error) throw error;
  return data || [];
}

export async function crmActividadesRecientes(limite = 50) {
  const { data, error } = await supabase.from('crm_actividades')
    .select('*').order('fecha', { ascending: false }).limit(limite);
  if (error) throw error;
  return data || [];
}

export async function crmEventosCuenta(cuentaId) {
  const { data, error } = await supabase.from('crm_eventos')
    .select('*').eq('cuenta_id', cuentaId).order('at', { ascending: false }).limit(100);
  if (error) throw error;
  return data || [];
}

export async function crmRegistrarActividad({ cuentaId, tipo, resumen, proyectoId = null, fecha = null,
  siguienteAccion = null, siguienteFecha = null, nuevaEtapa = null, motivoPerdida = null }) {
  const { data, error } = await supabase.rpc('crm_registrar_actividad', {
    p_cuenta_id: cuentaId, p_tipo: tipo, p_resumen: resumen, p_proyecto_id: proyectoId,
    p_fecha: fecha, p_siguiente_accion: siguienteAccion, p_siguiente_fecha: siguienteFecha,
    p_nueva_etapa: nuevaEtapa, p_motivo_perdida: motivoPerdida,
  });
  if (error) throw error;
  return data;
}

export async function crmResumenEquipo(desde) {
  const { data, error } = await supabase.rpc('crm_resumen_equipo', desde ? { p_desde: desde } : {});
  if (error) throw error;
  return data || [];
}

export async function crmResponsables() {
  const { data, error } = await supabase.rpc('crm_responsables');
  if (error) throw error;
  return data || [];
}

// ═══════════════════════════════════════════════════════════════════════════
// CRM 2 · CASOS (28-sep-2026) — seguimiento de pacientes referidos.
// Lectura: staff (RLS). Escritura: SOLO por RPC; la etapa la deriva la base.
// ═══════════════════════════════════════════════════════════════════════════

// Casos recibidos desde `desde` + todos los que siguen abiertos (sin importar fecha).
export async function casosListar(desde) {
  const { data, error } = await supabase.from('casos').select('*')
    .or(`recibido_at.gte.${desde},etapa.in.(recibido,contactado,cotizado,agendado,pausa)`)
    .order('recibido_at', { ascending: false }).limit(1000);
  if (error) throw error;
  return data || [];
}

export async function casosEmbudo(desde, hasta = null) {
  const { data, error } = await supabase.rpc('casos_embudo', { p_desde: desde, p_hasta: hasta });
  if (error) throw error;
  return data || {};
}

export async function casosMedicos() {
  const { data, error } = await supabase.rpc('casos_medicos');
  if (error) throw error;
  return data || [];
}

export async function casosPersonal() {
  const { data, error } = await supabase.rpc('casos_personal');
  if (error) throw error;
  return data || [];
}

export async function casoEventos(casoId) {
  const { data, error } = await supabase.from('caso_eventos').select('*').eq('caso_id', casoId).order('at');
  if (error) throw error;
  return data || [];
}

export async function casoItems(casoId) {
  const { data, error } = await supabase.from('caso_items').select('*').eq('caso_id', casoId).order('created_at');
  if (error) throw error;
  return data || [];
}

export async function casoCrearPapel({ canal, pacienteNombre, pacienteTelefono, cuentaId = null, medicoTexto = null,
  prioridad = 'rutina', estudios = [], notas = null }) {
  const { data, error } = await supabase.rpc('caso_crear_papel', {
    p_canal: canal, p_paciente_nombre: pacienteNombre, p_paciente_telefono: pacienteTelefono,
    p_cuenta_id: cuentaId, p_medico_texto: medicoTexto, p_prioridad: prioridad,
    p_estudios: estudios, p_notas: notas,
  });
  if (error) throw error;
  return data;
}

export async function casoEvento(casoId, tipo, detalle = null, dato = {}) {
  const { error } = await supabase.rpc('caso_evento', { p_caso: casoId, p_tipo: tipo, p_detalle: detalle, p_dato: dato });
  if (error) throw error;
}

export async function casoVincularOrden(casoId, correlativo) {
  const { error } = await supabase.rpc('caso_vincular_orden', { p_caso: casoId, p_correlativo: correlativo });
  if (error) throw error;
}

// Al descartar un pase, el motivo va al caso que el pase ya creó.
export async function casoMotivoDePase(solicitudId, motivo, detalle = null) {
  const { data, error } = await supabase.from('casos').select('id').eq('solicitud_id', solicitudId).maybeSingle();
  if (error) throw error;
  if (!data) return;
  await casoEvento(data.id, 'motivo', detalle, { motivo });
}

// ═══════════════════════════════════════════════════════════════════════════
// LABORATORIO DE EMBRIOLOGÍA (Reprolab) — hojas TVA/FET, inventario, control
// Todo por RPC SECURITY DEFINER: las tablas fert_lab_* están cerradas.
// ═══════════════════════════════════════════════════════════════════════════

export async function getBandejaLab() {
  const { data, error } = await supabase.rpc('lab_bandeja');
  if (error) throw error;
  return data || [];
}

export async function crearHojaLab(cicloId, tipo) {
  const { data, error } = await supabase.rpc('lab_crear_hoja', { p_ciclo_id: cicloId, p_tipo: tipo });
  if (error) throw error;
  return data;
}

export async function getHojaLab(hojaId) {
  const { data, error } = await supabase.rpc('lab_get_hoja', { p_hoja_id: hojaId });
  if (error) throw error;
  return data || null;
}

/** ruta: ['semen','pre','vol']. Vacío / false = quitar el dato. */
export async function setDatoLab(hojaId, ruta, valor) {
  const { data, error } = await supabase.rpc('lab_set_dato', {
    p_hoja_id: hojaId, p_ruta: ruta, p_valor: valor === undefined ? null : valor,
  });
  if (error) throw error;
  return data;
}

export async function setNumOvocitosLab(hojaId, n) {
  const { data, error } = await supabase.rpc('lab_set_num_ovocitos', { p_hoja_id: hojaId, p_n: n });
  if (error) throw error;
  return data;
}

export async function setOvocitoLab(hojaId, numero, dia, campo, valor) {
  const { data, error } = await supabase.rpc('lab_set_ovocito', {
    p_hoja_id: hojaId, p_numero: numero, p_dia: dia, p_campo: campo,
    p_valor: valor === undefined ? null : valor,
  });
  if (error) throw error;
  return data;
}

export async function setCrioLab(crioId, campo, valor) {
  const { error } = await supabase.rpc('lab_set_crio', { p_crio_id: crioId, p_campo: campo, p_valor: valor ?? null });
  if (error) throw error;
}

/** campo: 'usar' | 'recuperado' | 'transferido' */
export async function marcarFetLab(hojaId, crioId, campo, valor) {
  const { error } = await supabase.rpc('lab_fet_marcar', {
    p_hoja_id: hojaId, p_crio_id: crioId, p_campo: campo, p_valor: !!valor,
  });
  if (error) throw error;
}

export async function cerrarHojaLab(hojaId, cerrar) {
  const { error } = await supabase.rpc('lab_cerrar_hoja', { p_hoja_id: hojaId, p_cerrar: !!cerrar });
  if (error) throw error;
}

export async function getInventarioLab() {
  const { data, error } = await supabase.rpc('lab_inventario');
  if (error) throw error;
  return data || [];
}

export async function getControlLab(desde = null, hasta = null) {
  const { data, error } = await supabase.rpc('lab_control', { p_desde: desde || null, p_hasta: hasta || null });
  if (error) throw error;
  return data || [];
}

export async function getNotasPacienteLab(medPacienteId) {
  const { data, error } = await supabase.rpc('lab_notas_paciente', { p_med_paciente_id: medPacienteId });
  if (error) throw error;
  return data || [];
}

/** Para el médico: hojas del laboratorio de un ciclo (solo lectura). */
export async function getHojasLabDeCiclo(cicloId) {
  const { data, error } = await supabase.rpc('lab_hojas_de_ciclo', { p_ciclo_id: cicloId });
  if (error) return [];
  return data || [];
}

/** Mensaje legible para los errores de las RPC del laboratorio. */
export function errorLab(e) {
  const m = String(e?.message || e || '');
  if (m.includes('hoja_cerrada')) return 'La hoja está cerrada. Reabrila para hacer cambios.';
  if (m.includes('sin_permiso')) return 'No tienes permiso para modificar esta hoja.';
  if (m.includes('ovocitos_con_datos')) return 'No se puede bajar el número: hay ovocitos con datos en esas columnas.';
  if (m.includes('tva_solo_en_alta')) return 'La hoja TVA solo se abre en un ciclo de alta complejidad.';
  if (m.includes('crio_usado_en_otra_hoja')) return 'Ese cryotop ya se desvitrificó en otra hoja.';
  if (m.includes('no_recuperado')) return 'Marca primero que el embrión se recuperó.';
  return m;
}
